import React from 'react';
import { 
  Crown, Globe, MapPin, Sparkles, Megaphone, User,
  Briefcase, Users, TrendingUp, Heart, Palette, Target,
  Building2, Zap, Wrench, Shield
} from 'lucide-react';
import { DESIGN } from '../DesignSystem';
import { COGNITIVE_LANES } from '../../mock';

// ================================================
// ROLE DEFINITIONS
// ================================================
export const DEMO_ROLES = {
  ceo: {
    id: 'ceo',
    title: 'CEO',
    icon: Crown,
    lane: 'Strategy & Architecture',
    level: 'L4',
    description: 'Strategy & Architecture · L4',
    isUniqueIP: false,
    isChiefOfStaff: false
  },
  chief_of_staff: {
    id: 'chief_of_staff',
    title: 'Chief of Staff',
    icon: Shield,
    lane: 'Operations & Coordination',
    level: 'L3-L4',
    description: 'Executive Coordination · L3-L4',
    isUniqueIP: false,
    isChiefOfStaff: true
  },
  national_director: {
    id: 'national_director',
    title: 'National Director',
    icon: Globe,
    lane: 'Strategy & Architecture',
    level: 'L4',
    description: 'Strategy & Architecture · L4',
    isUniqueIP: false,
    isChiefOfStaff: false
  },
  regional_director: {
    id: 'regional_director',
    title: 'Regional Director',
    icon: MapPin,
    lane: 'Operations',
    level: 'L3-L4',
    description: 'Operations · L3-L4',
    isUniqueIP: false,
    isChiefOfStaff: false
  },
  unique_ip_director: {
    id: 'unique_ip_director',
    title: 'Unique IP Director',
    icon: Sparkles,
    lane: 'Unique IP Division',
    level: 'L3',
    description: 'Soul Food · Unique IP Division · L3',
    isUniqueIP: true,
    isChiefOfStaff: false
  },
  unique_ip_operations: {
    id: 'unique_ip_operations',
    title: 'Unique IP Ops',
    icon: Wrench,
    lane: 'Operations',
    level: 'L1-L2',
    description: 'Soul Food · Operations · L1-L2',
    isUniqueIP: true,
    isChiefOfStaff: false
  },
  marketing_manager: {
    id: 'marketing_manager',
    title: 'Marketing Manager',
    icon: Megaphone,
    lane: 'Marketing',
    level: 'L2-L3',
    description: 'Marketing · L2-L3',
    isUniqueIP: false,
    isChiefOfStaff: false
  },
  team_member: {
    id: 'team_member',
    title: 'Team Member',
    icon: User,
    lane: 'Marketing',
    level: 'L1',
    description: 'Marketing Lane · L1',
    isUniqueIP: false,
    isChiefOfStaff: false
  }
};

export const ROLE_ORDER = [
  'ceo',
  'chief_of_staff',
  'national_director', 
  'regional_director',
  'unique_ip_director',
  'unique_ip_operations',
  'marketing_manager',
  'team_member'
];

// ================================================
// UNIQUE IP COLOR PALETTE (Amber/Gold)
// ================================================
export const UNIQUE_IP_COLORS = {
  primary: '#F59E0B',
  primaryDark: '#D97706',
  primaryLight: '#FBBF24',
  primaryGlow: 'rgba(245, 158, 11, 0.3)',
  primarySubtle: 'rgba(245, 158, 11, 0.08)',
  accent: '#EAB308',
  accentGlow: 'rgba(234, 179, 8, 0.3)'
};

// ================================================
// CHIEF OF STAFF COLOR PALETTE (Royal Purple)
// ================================================
export const CHIEF_OF_STAFF_COLORS = {
  primary: '#8B5CF6',
  primaryDark: '#7C3AED',
  primaryLight: '#A78BFA',
  primaryGlow: 'rgba(139, 92, 246, 0.3)',
  primarySubtle: 'rgba(139, 92, 246, 0.08)',
  accent: '#9333EA',
  accentGlow: 'rgba(147, 51, 234, 0.3)'
};

// ================================================
// DEMO ROLE SWITCHER COMPONENT
// ================================================
export const DemoRoleSwitcher = ({ activeRole, onRoleChange }) => {
  const activeRoleData = DEMO_ROLES[activeRole];
  const isUniqueIP = activeRoleData?.isUniqueIP;
  const isChiefOfStaff = activeRoleData?.isChiefOfStaff;
  const accentColor = isChiefOfStaff 
    ? CHIEF_OF_STAFF_COLORS.primary 
    : isUniqueIP 
      ? UNIQUE_IP_COLORS.primary 
      : DESIGN.colors.primary;

  return (
    <div className="mb-8" data-testid="demo-role-switcher">
      {/* Demo Mode Banner */}
      <div 
        className="flex items-center justify-center gap-3 mb-4 py-3 px-4 rounded-xl"
        style={{ 
          backgroundColor: isChiefOfStaff 
            ? CHIEF_OF_STAFF_COLORS.primarySubtle 
            : isUniqueIP 
              ? UNIQUE_IP_COLORS.primarySubtle 
              : DESIGN.colors.primarySubtle,
          border: `1px solid ${accentColor}20`
        }}
      >
        <Sparkles className="w-4 h-4" style={{ color: accentColor }} />
        <span className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
          Explore how CognitiveOS personalizes the Command Center for every role in your organization — from the CEO to the ground-level team member, and across every type of business unit including Unique IP divisions.
        </span>
        <span 
          className="text-xs font-bold px-3 py-1 rounded-full flex-shrink-0"
          style={{ 
            backgroundColor: accentColor,
            color: 'white'
          }}
        >
          Demo Mode
        </span>
      </div>

      {/* Role Tab Bar */}
      <div 
        className="relative p-1.5 rounded-2xl"
        style={{ 
          backgroundColor: DESIGN.colors.background,
          boxShadow: DESIGN.shadows.md
        }}
      >
        <div className="flex gap-1">
          {ROLE_ORDER.map((roleId) => {
            const role = DEMO_ROLES[roleId];
            const Icon = role.icon;
            const isActive = activeRole === roleId;
            const roleIsUniqueIP = role.isUniqueIP;
            const roleIsChiefOfStaff = role.isChiefOfStaff;
            const roleAccent = roleIsChiefOfStaff 
              ? CHIEF_OF_STAFF_COLORS.primary 
              : roleIsUniqueIP 
                ? UNIQUE_IP_COLORS.primary 
                : DESIGN.colors.primary;

            return (
              <button
                key={roleId}
                onClick={() => onRoleChange(roleId)}
                className={`
                  relative flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm
                  transition-all duration-300 flex-1 justify-center
                  ${isActive ? 'text-white' : 'hover:bg-white/50'}
                `}
                style={{
                  backgroundColor: isActive ? roleAccent : 'transparent',
                  color: isActive ? 'white' : DESIGN.colors.textSecondary,
                  boxShadow: isActive 
                    ? `0 4px 15px ${roleAccent}40, 0 2px 5px ${roleAccent}30` 
                    : 'none'
                }}
                data-testid={`role-tab-${roleId}`}
              >
                {/* Special role indicator dot when not active */}
                {(roleIsUniqueIP || roleIsChiefOfStaff) && !isActive && (
                  <div 
                    className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full"
                    style={{ backgroundColor: roleAccent }}
                  />
                )}
                <Icon className="w-4 h-4" />
                <span className="hidden lg:inline">{role.title}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ================================================
// ROLE-SPECIFIC DATA GENERATORS
// ================================================

// CEO Data (kept as-is, uses API data)
export const getCEOData = () => null; // Uses actual API data

// National Director Data
export const getNationalDirectorData = () => ({
  daily_summary: {
    date: new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
    total_items: 9,
    completed_items: 2,
    high_priority_remaining: 4,
    meetings_count: 5,
    progress_percent: 22,
    focus_score: 72,
    cognitive_load_level: 'high'
  },
  focus_items: [
    // Morning
    {
      id: 'nd-1',
      title: 'Regional Performance Review',
      description: 'Weekly metrics synthesis across all regions with trend analysis and performance benchmarking',
      time: '8:00 AM',
      duration: '90 min',
      priority: 'high',
      type: 'task',
      icon: 'TrendingUp',
      color: '#3B82F6',
      completed: false,
      source: 'AI Strategic',
      participants: ['Regional Directors']
    },
    {
      id: 'nd-2',
      title: 'National Director Sync',
      description: 'Alignment meeting with peer directors on cross-functional initiatives and resource coordination',
      time: '10:00 AM',
      duration: '60 min',
      priority: 'high',
      type: 'meeting',
      icon: 'Users',
      color: DESIGN.colors.primary,
      completed: false,
      participants: ['CFO', 'COO', 'CMO']
    },
    {
      id: 'nd-3',
      title: 'Critical Phase Escalation Review',
      description: 'Overdue phases escalated from regional directors requiring national-level intervention and decision',
      time: '11:30 AM',
      duration: '45 min',
      priority: 'critical',
      type: 'task',
      icon: 'AlertTriangle',
      color: '#EF4444',
      completed: false,
      source: 'Escalated'
    },
    // Midday
    {
      id: 'nd-4',
      title: 'Q2 Budget Allocation Review',
      description: 'Resource distribution review across national departments with variance analysis',
      time: '12:30 PM',
      duration: '60 min',
      priority: 'high',
      type: 'task',
      icon: 'Target',
      color: '#8B5CF6',
      completed: false,
      participants: ['Finance Lead', 'Dept Heads']
    },
    {
      id: 'nd-5',
      title: 'Strategic Initiative Check-in',
      description: 'Progress review on national-level projects and strategic initiatives',
      time: '1:30 PM',
      duration: '45 min',
      priority: 'medium',
      type: 'meeting',
      icon: 'CheckCircle2',
      color: DESIGN.colors.primary,
      completed: true
    },
    // Afternoon
    {
      id: 'nd-6',
      title: 'Regional Director One-on-Ones',
      description: 'Structured check-ins with regional directors on blockers, team health, and priorities',
      time: '2:30 PM',
      duration: '120 min',
      priority: 'high',
      type: 'meeting',
      icon: 'Users',
      color: '#22C55E',
      completed: false,
      participants: ['East RD', 'West RD', 'Central RD', 'South RD']
    },
    {
      id: 'nd-7',
      title: 'Executive Briefing Preparation',
      description: 'Compiling national performance summary and strategic recommendations for C-Suite review',
      time: '5:00 PM',
      duration: '60 min',
      priority: 'high',
      type: 'task',
      icon: 'Target',
      color: '#1E3A5F',
      completed: false,
      source: 'AI Strategic'
    }
  ],
  alerts: [
    { id: 'nda-1', title: 'West Region: 3 overdue phases', severity: 'high', type: 'deadline' },
    { id: 'nda-2', title: 'Cross-functional resource conflict', severity: 'high', type: 'resource' },
    { id: 'nda-3', title: 'Strategic project flagged for focus', severity: 'medium', type: 'project' },
    { id: 'nda-4', title: 'Q2 budget variance exceeds 8%', severity: 'medium', type: 'financial' }
  ],
  priority_recommendations: [
    { id: 'ndr-1', title: 'Reallocate East resources to West', impact: 'high' },
    { id: 'ndr-2', title: 'Schedule emergency regional sync', impact: 'high' }
  ],
  quick_team: [
    { id: 'ndt-1', name: 'Sarah Martinez', avatar: 'SM', title: 'East Regional Director', active_tasks: 12, availability_status: 'available', cognitive_load: 75 },
    { id: 'ndt-2', name: 'James Chen', avatar: 'JC', title: 'West Regional Director', active_tasks: 15, availability_status: 'busy', cognitive_load: 88 },
    { id: 'ndt-3', name: 'Priya Patel', avatar: 'PP', title: 'Central Regional Director', active_tasks: 10, availability_status: 'available', cognitive_load: 62 },
    { id: 'ndt-4', name: 'Marcus Williams', avatar: 'MW', title: 'National Operations Lead', active_tasks: 8, availability_status: 'available', cognitive_load: 58 }
  ],
  burnout_alerts: [
    { member_name: 'James Chen', risk_score: 88, role: 'West Regional Director', trend: 'increasing', days_to_critical: 3 },
    { member_name: 'Elena Rodriguez', risk_score: 82, role: 'South Regional Director', trend: 'stable', days_to_critical: 7 }
  ],
  executive_reviews: [
    { id: 'nder-1', name: 'National Marketing Campaign Q2', type: 'Strategic Initiative', status: 'pending_review', submitted_by: 'CMO', days_pending: 2 },
    { id: 'nder-2', name: 'Cross-Regional Resource Reallocation', type: 'Budget Request', status: 'pending_review', submitted_by: 'Operations', days_pending: 1 }
  ],
  quick_actions: [
    { key: 'regional_sync', label: 'Schedule Regional Sync', icon: 'Calendar' },
    { key: 'escalate', label: 'Escalate to C-Suite', icon: 'TrendingUp' },
    { key: 'metrics', label: 'Review National Metrics', icon: 'Target' },
    { key: 'reallocate', label: 'Reallocate Resources', icon: 'Users' }
  ]
});

// Regional Director Data
export const getRegionalDirectorData = () => ({
  daily_summary: {
    date: new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
    total_items: 9,
    completed_items: 1,
    high_priority_remaining: 3,
    meetings_count: 5,
    progress_percent: 11,
    focus_score: 68,
    cognitive_load_level: 'elevated'
  },
  focus_items: [
    // Morning
    {
      id: 'rd-1',
      title: 'Regional Team Standup',
      description: 'Daily sync with all functional managers on priorities, blockers, and team health',
      time: '8:30 AM',
      duration: '30 min',
      priority: 'high',
      type: 'meeting',
      icon: 'Users',
      color: '#22C55E',
      completed: true,
      participants: ['Marketing Mgr', 'Ops Mgr', 'Sales Mgr', 'Finance Mgr']
    },
    {
      id: 'rd-oneone',
      title: '1:1 with Sarah Chen',
      description: 'Weekly check-in - discuss project progress, capacity, and career development',
      time: '9:00 AM',
      duration: '30 min',
      priority: 'high',
      type: 'meeting',
      icon: 'Users',
      color: '#8B5CF6',
      completed: false,
      // This is a 1:1 meeting with a team member - includes full member data
      participants: [{
        id: 'sarah-chen',
        name: 'Sarah Chen',
        title: 'Head of Product',
        avatar: 'SC',
        current_load: 6,
        max_task_load: 8,
        bio: 'Product strategy and roadmap ownership',
        lane_proficiency: {
          strategy: 'L4',
          operations: 'L3',
          creative: 'L2'
        }
      }]
    },
    {
      id: 'rd-2',
      title: 'Critical Staff Alert: Alex Kim',
      description: 'Team member showing cognitive load above 85% - requires immediate check-in and workload review',
      time: '9:30 AM',
      duration: '30 min',
      priority: 'critical',
      type: 'task',
      icon: 'AlertTriangle',
      color: '#EF4444',
      completed: false,
      source: 'AI Alert'
    },
    {
      id: 'rd-3',
      title: 'Phase Deadline Review',
      description: 'Review overdue project phases within the region and create action plans',
      time: '10:00 AM',
      duration: '45 min',
      priority: 'high',
      type: 'task',
      icon: 'Clock',
      color: '#F59E0B',
      completed: false
    },
    // Midday
    {
      id: 'rd-4',
      title: 'Regional Client Engagement',
      description: 'Key regional account touchpoint - quarterly business review with Enterprise Client',
      time: '12:00 PM',
      duration: '60 min',
      priority: 'high',
      type: 'meeting',
      icon: 'Users',
      color: '#3B82F6',
      completed: false,
      participants: ['Client Team', 'Sales Lead']
    },
    {
      id: 'rd-5',
      title: 'Monthly Budget Reconciliation',
      description: 'Regional spend review against allocated budget with variance analysis',
      time: '1:30 PM',
      duration: '45 min',
      priority: 'medium',
      type: 'task',
      icon: 'Target',
      color: '#64748B',
      completed: false
    },
    // Afternoon
    {
      id: 'rd-6',
      title: 'Manager Performance Reviews',
      description: 'One-on-ones with functional managers on team performance, goals, and development',
      time: '2:30 PM',
      duration: '90 min',
      priority: 'medium',
      type: 'meeting',
      icon: 'Users',
      color: DESIGN.colors.primary,
      completed: false,
      participants: ['Marketing Mgr', 'Ops Mgr']
    },
    {
      id: 'rd-7',
      title: 'Regional Report Submission',
      description: 'Weekly data submission to national director with performance highlights and blockers',
      time: '4:30 PM',
      duration: '30 min',
      priority: 'high',
      type: 'task',
      icon: 'TrendingUp',
      color: '#1E3A5F',
      completed: false,
      source: 'Weekly'
    }
  ],
  alerts: [
    { id: 'rda-1', title: 'Marketing team at 95% capacity', severity: 'high', type: 'capacity' },
    { id: 'rda-2', title: '2 project phases overdue this week', severity: 'medium', type: 'deadline' },
    { id: 'rda-3', title: 'Open headcount: Senior Designer', severity: 'low', type: 'staffing' }
  ],
  priority_recommendations: [
    { id: 'rdr-1', title: 'Redistribute marketing workload', impact: 'high' },
    { id: 'rdr-2', title: 'Expedite hiring process', impact: 'medium' }
  ],
  quick_team: [
    { id: 'rdt-1', name: 'Jessica Park', avatar: 'JP', title: 'Marketing Manager', active_tasks: 14, availability_status: 'busy', cognitive_load: 82 },
    { id: 'rdt-2', name: 'David Thompson', avatar: 'DT', title: 'Operations Manager', active_tasks: 9, availability_status: 'available', cognitive_load: 65 },
    { id: 'rdt-3', name: 'Nina Gupta', avatar: 'NG', title: 'Sales Manager', active_tasks: 11, availability_status: 'available', cognitive_load: 70 },
    { id: 'rdt-4', name: 'Alex Kim', avatar: 'AK', title: 'Finance Manager', active_tasks: 8, availability_status: 'available', cognitive_load: 58 }
  ],
  burnout_alerts: [
    { member_name: 'Jessica Park', risk_score: 82, role: 'Marketing Manager', trend: 'increasing', days_to_critical: 5 },
    { member_name: 'Carlos Rivera', risk_score: 78, role: 'Creative Lead', trend: 'stable', days_to_critical: 10 }
  ],
  executive_reviews: [
    { id: 'rder-1', name: 'Regional Event Budget Increase', type: 'Budget Request', status: 'pending_review', submitted_by: 'Marketing', days_pending: 1 },
    { id: 'rder-2', name: 'New Vendor Contract', type: 'Contract Approval', status: 'pending_review', submitted_by: 'Operations', days_pending: 2 }
  ],
  quick_actions: [
    { key: 'manager_sync', label: 'Schedule Manager Sync', icon: 'Calendar' },
    { key: 'submit_report', label: 'Submit Regional Report', icon: 'TrendingUp' },
    { key: 'flag_issue', label: 'Flag Staff Issue', icon: 'AlertTriangle' },
    { key: 'regional_dashboard', label: 'View Regional Dashboard', icon: 'Target' }
  ]
});

// Unique IP Director Data (Soul Food)
export const getUniqueIPDirectorData = () => ({
  daily_summary: {
    date: new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
    total_items: 8,
    completed_items: 2,
    high_priority_remaining: 3,
    meetings_count: 4,
    progress_percent: 25,
    focus_score: 74,
    cognitive_load_level: 'elevated',
    // Unique IP specific metrics
    active_initiatives: 6,
    partnership_pipeline: 4,
    brand_health_score: 87
  },
  focus_items: [
    // Morning
    {
      id: 'uip-1',
      title: 'Soul Food Brand Review',
      description: 'Weekly brand performance metrics — social reach, engagement, and community growth across platforms',
      time: '9:00 AM',
      duration: '45 min',
      priority: 'high',
      type: 'task',
      icon: 'TrendingUp',
      color: UNIQUE_IP_COLORS.primary,
      completed: true,
      source: 'Brand Analytics'
    },
    {
      id: 'uip-2',
      title: 'Partnership Pipeline Meeting',
      description: 'Review active sponsorship and partnership negotiations with external brands and organizations',
      time: '10:00 AM',
      duration: '60 min',
      priority: 'high',
      type: 'meeting',
      icon: 'Users',
      color: UNIQUE_IP_COLORS.primaryDark,
      completed: false,
      participants: ['Partnerships Lead', 'Legal', 'Finance']
    },
    {
      id: 'uip-3',
      title: 'Creative Direction Sync',
      description: 'Alignment meeting with the Soul Food creative team on campaign direction and upcoming content calendar',
      time: '11:30 AM',
      duration: '45 min',
      priority: 'high',
      type: 'meeting',
      icon: 'Target',
      color: '#A855F7',
      completed: false,
      participants: ['Creative Director', 'Content Lead', 'Design Lead']
    },
    // Midday
    {
      id: 'uip-4',
      title: 'Revenue Strategy Session',
      description: 'Review monetization performance across Soul Food revenue streams — events, merchandise, digital content, sponsorships',
      time: '12:30 PM',
      duration: '60 min',
      priority: 'high',
      type: 'meeting',
      icon: 'TrendingUp',
      color: '#EAB308',
      completed: false,
      participants: ['Revenue Lead', 'Events Manager', 'Merch Lead']
    },
    {
      id: 'uip-5',
      title: 'Talent & Culture Check-in',
      description: 'Pulse check on Soul Food team energy and creative morale — team energy directly affects output quality',
      time: '1:45 PM',
      duration: '30 min',
      priority: 'medium',
      type: 'task',
      icon: 'Users',
      color: '#F472B6',
      completed: true,
      source: 'Culture'
    },
    // Afternoon
    {
      id: 'uip-6',
      title: 'External Brand Meeting',
      description: 'Relationship meeting with potential strategic partner interested in Soul Food collaboration',
      time: '3:00 PM',
      duration: '60 min',
      priority: 'critical',
      type: 'meeting',
      icon: 'Users',
      color: UNIQUE_IP_COLORS.primary,
      completed: false,
      participants: ['Nike Brand Team', 'Partnerships Lead']
    },
    {
      id: 'uip-7',
      title: 'Content Review & Approval',
      description: 'Final approval on creative assets going live this week across Soul Food platforms',
      time: '4:30 PM',
      duration: '45 min',
      priority: 'high',
      type: 'task',
      icon: 'CheckCircle2',
      color: '#A855F7',
      completed: false,
      source: 'Creative'
    },
    {
      id: 'uip-8',
      title: 'Weekly Soul Food Report',
      description: 'Compile brand performance summary for Executive Director and national C-Suite review',
      time: '5:30 PM',
      duration: '30 min',
      priority: 'medium',
      type: 'task',
      icon: 'Target',
      color: UNIQUE_IP_COLORS.primaryDark,
      completed: false,
      source: 'Weekly'
    }
  ],
  alerts: [
    { id: 'uipa-1', title: 'Nike partnership response deadline in 48hrs', severity: 'critical', type: 'partnership' },
    { id: 'uipa-2', title: 'Video content 2 days behind launch schedule', severity: 'high', type: 'content' },
    { id: 'uipa-3', title: 'Creative Lead showing signs of creative burnout', severity: 'high', type: 'team' },
    { id: 'uipa-4', title: 'Adidas collab opportunity - 1 week response window', severity: 'medium', type: 'opportunity' }
  ],
  priority_recommendations: [
    { id: 'uipr-1', title: 'Prioritize Nike partnership decision', impact: 'critical' },
    { id: 'uipr-2', title: 'Check in with Creative Lead on workload', impact: 'high' }
  ],
  quick_team: [
    { id: 'uipt-1', name: 'Maya Johnson', avatar: 'MJ', title: 'Chief of Staff', active_tasks: 8, availability_status: 'available', cognitive_load: 68 },
    { id: 'uipt-2', name: 'Kai Williams', avatar: 'KW', title: 'Creative Director', active_tasks: 12, availability_status: 'busy', cognitive_load: 85 },
    { id: 'uipt-3', name: 'Jordan Lee', avatar: 'JL', title: 'Partnerships Lead', active_tasks: 6, availability_status: 'available', cognitive_load: 62 },
    { id: 'uipt-4', name: 'Ava Chen', avatar: 'AC', title: 'Content Lead', active_tasks: 10, availability_status: 'available', cognitive_load: 72 }
  ],
  burnout_alerts: [
    { member_name: 'Kai Williams', risk_score: 85, role: 'Creative Director', trend: 'increasing', days_to_critical: 4, note: 'Signs of creative exhaustion — reduced output quality and longer turnaround times' },
    { member_name: 'Ava Chen', risk_score: 72, role: 'Content Lead', trend: 'stable', days_to_critical: 12 }
  ],
  executive_reviews: [
    { id: 'uiper-1', name: 'Summer Campaign Launch', type: 'Creative Campaign', status: 'pending_review', submitted_by: 'Creative Director', days_pending: 1 },
    { id: 'uiper-2', name: 'Nike Partnership Agreement', type: 'Partnership Contract', status: 'pending_review', submitted_by: 'Partnerships Lead', days_pending: 2 },
    { id: 'uiper-3', name: 'Pop-up Event Budget', type: 'Budget Request', status: 'pending_review', submitted_by: 'Events', days_pending: 1 }
  ],
  quick_actions: [
    { key: 'launch_campaign', label: 'Launch Campaign', icon: 'Sparkles' },
    { key: 'partnership', label: 'Initiate Partnership', icon: 'Users' },
    { key: 'brand_meeting', label: 'Schedule Brand Meeting', icon: 'Calendar' },
    { key: 'analytics', label: 'View Soul Food Analytics', icon: 'TrendingUp' }
  ]
});

// Marketing Manager Data
export const getMarketingManagerData = () => ({
  daily_summary: {
    date: new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
    total_items: 8,
    completed_items: 2,
    high_priority_remaining: 2,
    meetings_count: 3,
    progress_percent: 25,
    focus_score: 65,
    cognitive_load_level: 'elevated'
  },
  focus_items: [
    // Morning
    {
      id: 'mm-1',
      title: 'Campaign Performance Analysis',
      description: 'Weekly metrics review with reporting to regional director on campaign ROI and engagement',
      time: '8:30 AM',
      duration: '45 min',
      priority: 'high',
      type: 'task',
      icon: 'TrendingUp',
      color: '#FB923C',
      completed: true,
      source: 'Analytics'
    },
    {
      id: 'mm-2',
      title: 'Content Calendar Approval',
      description: 'Reviewing and scheduling social content for the week across all regional channels',
      time: '9:30 AM',
      duration: '30 min',
      priority: 'high',
      type: 'task',
      icon: 'CheckCircle2',
      color: '#A855F7',
      completed: false
    },
    {
      id: 'mm-3',
      title: 'Team Check-in',
      description: 'Morning sync with marketing team on priorities, blockers, and task assignments',
      time: '10:15 AM',
      duration: '30 min',
      priority: 'medium',
      type: 'meeting',
      icon: 'Users',
      color: DESIGN.colors.primary,
      completed: true,
      participants: ['Brand Lead', 'Digital Lead', 'Content Lead']
    },
    // Midday
    {
      id: 'mm-4',
      title: 'Creative Asset Review',
      description: 'Approving campaign visuals and copy from Brand Lead before client review',
      time: '12:00 PM',
      duration: '45 min',
      priority: 'high',
      type: 'task',
      icon: 'Target',
      color: '#A855F7',
      completed: false,
      source: 'Brand Lead'
    },
    {
      id: 'mm-5',
      title: 'Media Vendor Coordination',
      description: 'Regional advertising placement sync with media buying partner',
      time: '1:00 PM',
      duration: '30 min',
      priority: 'medium',
      type: 'meeting',
      icon: 'Phone',
      color: '#3B82F6',
      completed: false,
      participants: ['Media Agency']
    },
    // Afternoon
    {
      id: 'mm-6',
      title: 'Social Performance Monitoring',
      description: 'Engagement review across platforms and response management for key interactions',
      time: '2:30 PM',
      duration: '45 min',
      priority: 'medium',
      type: 'task',
      icon: 'TrendingUp',
      color: '#FB923C',
      completed: false
    },
    {
      id: 'mm-7',
      title: 'Weekly Marketing Report',
      description: 'Compile regional marketing metrics for regional director weekly review',
      time: '4:00 PM',
      duration: '45 min',
      priority: 'high',
      type: 'task',
      icon: 'Target',
      color: '#22C55E',
      completed: false,
      source: 'Weekly'
    },
    {
      id: 'mm-8',
      title: 'Campaign Planning Session',
      description: 'Briefing the team on next week campaign priorities and creative needs',
      time: '5:00 PM',
      duration: '30 min',
      priority: 'medium',
      type: 'meeting',
      icon: 'Users',
      color: DESIGN.colors.primary,
      completed: false,
      participants: ['Full Marketing Team']
    }
  ],
  alerts: [
    { id: 'mma-1', title: 'Social content for Friday not submitted', severity: 'high', type: 'content' },
    { id: 'mma-2', title: 'Campaign CTR 15% below target', severity: 'medium', type: 'performance' },
    { id: 'mma-3', title: '3 creative approvals pending your review', severity: 'medium', type: 'approval' }
  ],
  priority_recommendations: [
    { id: 'mmr-1', title: 'Follow up on Friday content delivery', impact: 'high' },
    { id: 'mmr-2', title: 'Review underperforming campaign creative', impact: 'medium' }
  ],
  quick_team: [
    { id: 'mmt-1', name: 'Taylor Brooks', avatar: 'TB', title: 'Brand Lead', active_tasks: 8, availability_status: 'available', cognitive_load: 72 },
    { id: 'mmt-2', name: 'Jordan Hayes', avatar: 'JH', title: 'Digital Lead', active_tasks: 10, availability_status: 'busy', cognitive_load: 78 },
    { id: 'mmt-3', name: 'Riley Morgan', avatar: 'RM', title: 'Content Lead', active_tasks: 6, availability_status: 'available', cognitive_load: 65 },
    { id: 'mmt-4', name: 'Casey Quinn', avatar: 'CQ', title: 'Social Media Coordinator', active_tasks: 12, availability_status: 'available', cognitive_load: 80 }
  ],
  burnout_alerts: [
    { member_name: 'Casey Quinn', risk_score: 80, role: 'Social Media Coordinator', trend: 'increasing', days_to_critical: 6 }
  ],
  executive_reviews: [
    { id: 'mmer-1', name: 'Q2 Campaign Creative Package', type: 'Creative Approval', status: 'pending_review', submitted_by: 'Brand Lead', days_pending: 1 }
  ],
  quick_actions: [
    { key: 'launch_campaign', label: 'Launch Campaign', icon: 'Sparkles' },
    { key: 'approve_content', label: 'Approve Content', icon: 'CheckCircle2' },
    { key: 'request_brief', label: 'Request Creative Brief', icon: 'Target' },
    { key: 'analytics', label: 'View Marketing Analytics', icon: 'TrendingUp' }
  ]
});

// Team Member Data
export const getTeamMemberData = () => ({
  daily_summary: {
    date: new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
    total_items: 7,
    completed_items: 1,
    high_priority_remaining: 2,
    meetings_count: 2,
    progress_percent: 14,
    focus_score: 58,
    cognitive_load_level: 'optimal',
    // Personal metrics
    my_tasks: 7,
    my_deadlines_today: 3,
    my_load_level: 'moderate'
  },
  focus_items: [
    // Morning
    {
      id: 'tm-1',
      title: 'Write Instagram Caption Set',
      description: 'Create 5 captions for this week\'s product campaign following brand guidelines',
      time: '9:00 AM',
      duration: '60 min',
      priority: 'high',
      type: 'task',
      icon: 'Target',
      color: '#FB923C',
      completed: false,
      source: 'Assigned'
    },
    {
      id: 'tm-2',
      title: 'Resize Campaign Assets',
      description: 'Adapt approved banner creative to required platform dimensions (FB, IG, LinkedIn)',
      time: '10:15 AM',
      duration: '45 min',
      priority: 'high',
      type: 'task',
      icon: 'Target',
      color: '#A855F7',
      completed: true
    },
    {
      id: 'tm-3',
      title: 'Review Brand Guidelines Update',
      description: 'Read the updated brand standards document from the Brand Lead',
      time: '11:15 AM',
      duration: '20 min',
      priority: 'medium',
      type: 'task',
      icon: 'CheckCircle2',
      color: '#3B82F6',
      completed: false
    },
    // Midday
    {
      id: 'tm-4',
      title: 'Submit Draft Content',
      description: 'Upload completed social copy to shared drive for Brand Lead approval',
      time: '12:00 PM',
      duration: '15 min',
      priority: 'high',
      type: 'task',
      icon: 'TrendingUp',
      color: '#22C55E',
      completed: false
    },
    {
      id: 'tm-5',
      title: 'Attend Campaign Briefing',
      description: 'Join team meeting for next week campaign direction and assignments',
      time: '1:00 PM',
      duration: '30 min',
      priority: 'medium',
      type: 'meeting',
      icon: 'Users',
      color: DESIGN.colors.primary,
      completed: false,
      participants: ['Marketing Team']
    },
    // Afternoon
    {
      id: 'tm-6',
      title: 'Respond to Content Feedback',
      description: 'Address revision notes from Brand Lead on yesterday\'s caption submissions',
      time: '2:00 PM',
      duration: '45 min',
      priority: 'high',
      type: 'task',
      icon: 'MessageSquare',
      color: '#F59E0B',
      completed: false,
      source: 'Feedback'
    },
    {
      id: 'tm-7',
      title: 'Update Task Status & Plan Tomorrow',
      description: 'Mark completed items done, add progress notes, review tomorrow\'s assignments',
      time: '4:30 PM',
      duration: '20 min',
      priority: 'low',
      type: 'task',
      icon: 'CheckCircle2',
      color: DESIGN.colors.primary,
      completed: false
    }
  ],
  alerts: [
    { id: 'tma-1', title: 'Caption set due by end of day', severity: 'high', type: 'deadline' },
    { id: 'tma-2', title: 'Revision request pending response', severity: 'medium', type: 'feedback' },
    { id: 'tma-3', title: 'Campaign briefing in 2 hours', severity: 'low', type: 'reminder' }
  ],
  priority_recommendations: [
    { id: 'tmr-1', title: 'Complete caption set before noon', impact: 'high' },
    { id: 'tmr-2', title: 'Address feedback before briefing', impact: 'medium' }
  ],
  // "My Team" instead of "Quick Team"
  quick_team: [
    { id: 'tmt-1', name: 'Taylor Brooks', avatar: 'TB', title: 'Brand Lead (My Lead)', active_tasks: 8, availability_status: 'available', cognitive_load: 72 },
    { id: 'tmt-2', name: 'Jordan Hayes', avatar: 'JH', title: 'Digital Lead', active_tasks: 10, availability_status: 'busy', cognitive_load: 78 },
    { id: 'tmt-3', name: 'Jessica Park', avatar: 'JP', title: 'Marketing Manager', active_tasks: 14, availability_status: 'busy', cognitive_load: 82 }
  ],
  // No burnout alerts for team member role
  burnout_alerts: [],
  // No executive reviews for team member role
  executive_reviews: [],
  quick_actions: [
    { key: 'mark_complete', label: 'Mark Task Complete', icon: 'CheckCircle2' },
    { key: 'message_manager', label: 'Message My Manager', icon: 'MessageSquare' },
    { key: 'view_tasks', label: 'View My Tasks', icon: 'Target' },
    { key: 'request_extension', label: 'Request Deadline Extension', icon: 'Clock' }
  ],
  // Flag to indicate this is team member view
  isTeamMemberView: true
});

// ================================================
// UNIQUE IP OPERATIONS DATA (Soul Food Ground Level)
// ================================================
export const getUniqueIPOperationsData = () => ({
  daily_summary: {
    date: new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
    total_items: 8,
    completed_items: 2,
    high_priority_remaining: 3,
    meetings_count: 0,
    progress_percent: 25,
    focus_score: 72,
    cognitive_load_level: 'optimal',
    // Personal operational metrics
    my_tasks: 8,
    my_shifts: 1,
    todays_priority: 'Event Setup',
    my_load_level: 'active'
  },
  focus_items: [
    // Morning
    {
      id: 'uio-1',
      title: 'Venue Setup Checklist',
      description: 'Prepare the event space according to Soul Food brand standards before doors open — staging, signage, and ambient setup',
      time: '7:00 AM',
      duration: '90 min',
      priority: 'critical',
      type: 'task',
      icon: 'Wrench',
      color: UNIQUE_IP_COLORS.primary,
      completed: true,
      source: 'Operations Protocol'
    },
    {
      id: 'uio-2',
      title: 'Vendor Delivery Coordination',
      description: 'Receive and check in supply deliveries from three vendors — log inventory and confirm quantities against order sheets',
      time: '8:30 AM',
      duration: '45 min',
      priority: 'high',
      type: 'task',
      icon: 'Truck',
      color: UNIQUE_IP_COLORS.primaryDark,
      completed: true,
      source: 'Logistics'
    },
    {
      id: 'uio-3',
      title: 'Equipment Inventory Check',
      description: 'Verify all production equipment is present and functional before the day\'s activities begin — audio, lighting, displays',
      time: '9:30 AM',
      duration: '30 min',
      priority: 'high',
      type: 'task',
      icon: 'Clipboard',
      color: '#22C55E',
      completed: false,
      source: 'Pre-Event Checklist'
    },
    // Midday
    {
      id: 'uio-4',
      title: 'Event Support Rotation',
      description: 'On-the-ground operational support during the Soul Food midday activation — guest assistance, station monitoring, troubleshooting',
      time: '11:00 AM',
      duration: '180 min',
      priority: 'high',
      type: 'shift',
      icon: 'Users',
      color: UNIQUE_IP_COLORS.primary,
      completed: false,
      source: 'Shift Schedule'
    },
    {
      id: 'uio-5',
      title: 'Restock Supply Stations',
      description: 'Replenish branded materials and supplies at all stations per the operations lead\'s direction — napkins, cups, promotional materials',
      time: '2:00 PM',
      duration: '30 min',
      priority: 'medium',
      type: 'task',
      icon: 'Package',
      color: '#3B82F6',
      completed: false,
      source: 'Operations Lead'
    },
    // Afternoon
    {
      id: 'uio-6',
      title: 'Post-Event Breakdown',
      description: 'Break down the event space according to Soul Food operations protocol — pack equipment, clear signage, restore venue',
      time: '4:00 PM',
      duration: '90 min',
      priority: 'high',
      type: 'task',
      icon: 'Wrench',
      color: UNIQUE_IP_COLORS.primaryDark,
      completed: false,
      source: 'Operations Protocol'
    },
    {
      id: 'uio-7',
      title: 'Inventory Count and Reporting',
      description: 'Complete end-of-day inventory log and submit to operations lead — document any damaged or missing items',
      time: '5:30 PM',
      duration: '30 min',
      priority: 'high',
      type: 'task',
      icon: 'Clipboard',
      color: '#22C55E',
      completed: false,
      source: 'Daily Closeout'
    },
    {
      id: 'uio-8',
      title: 'Task Status Update',
      description: 'Mark all completed tasks done in CognitiveOS and flag anything incomplete to the supervisor',
      time: '6:00 PM',
      duration: '15 min',
      priority: 'medium',
      type: 'task',
      icon: 'CheckCircle2',
      color: UNIQUE_IP_COLORS.primary,
      completed: false,
      source: 'EOD'
    }
  ],
  // Personal and immediate alerts only
  alerts: [
    { id: 'uioa-1', title: 'Equipment check due in 30 minutes', severity: 'high', type: 'deadline' },
    { id: 'uioa-2', title: 'Confirm vendor delivery receipt with Marcus', severity: 'medium', type: 'confirmation' },
    { id: 'uioa-3', title: 'Tomorrow\'s event setup call time: 6:30 AM', severity: 'low', type: 'reminder' }
  ],
  priority_recommendations: [
    { id: 'uior-1', title: 'Complete equipment check before activation begins', impact: 'high' },
    { id: 'uior-2', title: 'Confirm delivery receipt before vendor leaves', impact: 'medium' }
  ],
  // "My Team" - immediate Soul Food operations team only
  quick_team: [
    { id: 'uiot-1', name: 'Marcus Thompson', avatar: 'MT', title: 'Operations Lead (My Lead)', active_tasks: 6, availability_status: 'available', cognitive_load: 65 },
    { id: 'uiot-2', name: 'Jasmine Rivera', avatar: 'JR', title: 'Operations Team Member', active_tasks: 7, availability_status: 'busy', cognitive_load: 70 },
    { id: 'uiot-3', name: 'Devon Jackson', avatar: 'DJ', title: 'Operations Team Member', active_tasks: 5, availability_status: 'available', cognitive_load: 55 },
    { id: 'uiot-4', name: 'Aaliyah Davis', avatar: 'AD', title: 'Event Coordinator', active_tasks: 8, availability_status: 'busy', cognitive_load: 72 }
  ],
  // No burnout alerts - ground level team member with no visibility
  burnout_alerts: [],
  // No executive reviews - ground level team member
  executive_reviews: [],
  // Hyper-specific quick actions for operations role
  quick_actions: [
    { key: 'log_inventory', label: 'Log Inventory', icon: 'Clipboard' },
    { key: 'message_ops_lead', label: 'Message Ops Lead', icon: 'MessageSquare' },
    { key: 'mark_complete', label: 'Mark Task Complete', icon: 'CheckCircle2' },
    { key: 'report_issue', label: 'Report Issue', icon: 'AlertTriangle' }
  ],
  // Flags to indicate this is operations view
  isTeamMemberView: true,
  isUniqueIPOperations: true,
  hideBurnoutAlerts: true,
  hideExecutiveReviews: true
});

// ================================================
// CHIEF OF STAFF DATA (Executive Coordination)
// ================================================
export const getChiefOfStaffData = () => ({
  daily_summary: {
    date: new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
    total_items: 12,
    completed_items: 3,
    high_priority_remaining: 5,
    meetings_count: 4,
    progress_percent: 25,
    focus_score: 78,
    cognitive_load_level: 'elevated',
    // Chief of Staff specific metrics
    items_requiring_action: 8,
    executive_calendar_items: 6,
    pending_followups: 12,
    decisions_awaiting: 4
  },
  focus_items: [
    // Morning - Executive Support
    {
      id: 'cos-1',
      title: 'Executive Briefing Preparation',
      description: "Compile the daily executive briefing document — yesterday's key completions, today's critical decisions, and three items requiring immediate executive attention",
      time: '7:30 AM',
      duration: '45 min',
      priority: 'critical',
      type: 'task',
      icon: 'Target',
      color: CHIEF_OF_STAFF_COLORS.primary,
      completed: true,
      source: 'Daily Protocol',
      entity: 'executive' // purple tag
    },
    {
      id: 'cos-2',
      title: 'Review Executive Calendar',
      description: "Confirm all of today's meetings are prepared with agendas, attendee lists, and relevant briefing materials",
      time: '8:15 AM',
      duration: '30 min',
      priority: 'high',
      type: 'task',
      icon: 'Calendar',
      color: CHIEF_OF_STAFF_COLORS.primaryDark,
      completed: true,
      source: 'Calendar Prep',
      entity: 'executive'
    },
    {
      id: 'cos-3',
      title: 'Soul Food Weekly Report Compilation',
      description: 'Aggregate Soul Food division performance data from the Unique IP Director for the executive\'s weekly review',
      time: '9:00 AM',
      duration: '30 min',
      priority: 'high',
      type: 'task',
      icon: 'TrendingUp',
      color: UNIQUE_IP_COLORS.primary,
      completed: false,
      source: 'Division Report',
      entity: 'soulfood' // amber tag
    },
    {
      id: 'cos-4',
      title: 'Board Meeting Prep Follow-Up',
      description: "Ensure all action items from last week's board meeting have been addressed and responses documented",
      time: '10:00 AM',
      duration: '45 min',
      priority: 'high',
      type: 'task',
      icon: 'CheckCircle2',
      color: '#1E3A5F',
      completed: false,
      source: 'Board Action Items',
      entity: 'company' // navy tag
    },
    // Midday - Cross-Department Coordination
    {
      id: 'cos-5',
      title: 'Cross-Department Coordination Call',
      description: 'Sync with all department heads to surface blockers and escalations before they reach the executive',
      time: '11:30 AM',
      duration: '60 min',
      priority: 'high',
      type: 'meeting',
      icon: 'Users',
      color: CHIEF_OF_STAFF_COLORS.primary,
      completed: false,
      participants: ['All Dept Heads'],
      entity: 'company'
    },
    {
      id: 'cos-6',
      title: 'Executive Review Pipeline Management',
      description: 'Review all items sitting in the executive review queue and follow up with submitters on anything over 24 hours old',
      time: '12:30 PM',
      duration: '45 min',
      priority: 'critical',
      type: 'task',
      icon: 'Target',
      color: CHIEF_OF_STAFF_COLORS.primaryDark,
      completed: false,
      source: 'Pipeline Review',
      entity: 'executive'
    },
    {
      id: 'cos-7',
      title: 'Soul Food Partnership Documentation',
      description: "Organize and file the latest partnership agreement documents for the executive's signature review",
      time: '1:30 PM',
      duration: '30 min',
      priority: 'medium',
      type: 'task',
      icon: 'FileText',
      color: UNIQUE_IP_COLORS.primary,
      completed: true,
      source: 'Legal/Partnerships',
      entity: 'soulfood'
    },
    // Afternoon - Communication & Closeout
    {
      id: 'cos-8',
      title: 'Meeting Notes Distribution',
      description: "Distribute this morning's executive sync notes to all attendees with action item assignments highlighted",
      time: '2:30 PM',
      duration: '30 min',
      priority: 'high',
      type: 'task',
      icon: 'MessageSquare',
      color: '#3B82F6',
      completed: false,
      source: 'Communication',
      entity: 'company'
    },
    {
      id: 'cos-9',
      title: 'Stakeholder Follow-Up',
      description: 'Send follow-up communications on behalf of the executive to three external stakeholders',
      time: '3:00 PM',
      duration: '45 min',
      priority: 'high',
      type: 'task',
      icon: 'Users',
      color: '#F472B6',
      completed: false,
      source: 'External Relations',
      entity: 'executive'
    },
    {
      id: 'cos-10',
      title: 'Decision Log Update',
      description: 'Update the organizational decision log with all decisions made today and their rationale',
      time: '4:00 PM',
      duration: '30 min',
      priority: 'medium',
      type: 'task',
      icon: 'Clipboard',
      color: CHIEF_OF_STAFF_COLORS.primary,
      completed: false,
      source: 'Governance',
      entity: 'company'
    },
    {
      id: 'cos-11',
      title: 'End of Day Executive Summary',
      description: "Prepare tomorrow's briefing document and flag anything that needs executive attention first thing in the morning",
      time: '5:00 PM',
      duration: '45 min',
      priority: 'high',
      type: 'task',
      icon: 'Target',
      color: CHIEF_OF_STAFF_COLORS.primaryDark,
      completed: false,
      source: 'EOD Prep',
      entity: 'executive'
    },
    {
      id: 'cos-12',
      title: 'Calendar Management',
      description: "Review next week's executive calendar and confirm all prep materials will be ready",
      time: '5:45 PM',
      duration: '30 min',
      priority: 'medium',
      type: 'task',
      icon: 'Calendar',
      color: CHIEF_OF_STAFF_COLORS.primary,
      completed: false,
      source: 'Weekly Prep',
      entity: 'executive'
    }
  ],
  // Coordination and administrative alerts
  alerts: [
    { id: 'cosa-1', title: 'Executive review item approaching 48hr SLA threshold', severity: 'critical', type: 'sla' },
    { id: 'cosa-2', title: 'Department head has not responded to follow-up for 3 days', severity: 'high', type: 'followup' },
    { id: 'cosa-3', title: 'Board member communication needs response by EOD', severity: 'high', type: 'communication' },
    { id: 'cosa-4', title: 'Soul Food event lacks confirmed logistics details', severity: 'medium', type: 'coordination' },
    { id: 'cosa-5', title: 'Team member flagged blocker requiring escalation', severity: 'medium', type: 'escalation' }
  ],
  priority_recommendations: [
    { id: 'cosr-1', title: 'Clear executive review backlog before EOD', impact: 'critical' },
    { id: 'cosr-2', title: 'Follow up with West Regional Director on overdue report', impact: 'high' },
    { id: 'cosr-3', title: 'Confirm Soul Food event logistics with operations team', impact: 'high' }
  ],
  // Key Team - Executive + All Department Heads + Soul Food Leadership
  quick_team: [
    { id: 'cost-1', name: 'Alexandra Chen', avatar: 'AC', title: 'CEO (Executive)', active_tasks: 5, availability_status: 'available', cognitive_load: 62, isExecutive: true },
    { id: 'cost-2', name: 'Sarah Martinez', avatar: 'SM', title: 'East Regional Director', active_tasks: 12, availability_status: 'available', cognitive_load: 75 },
    { id: 'cost-3', name: 'James Chen', avatar: 'JC', title: 'West Regional Director', active_tasks: 15, availability_status: 'busy', cognitive_load: 88 },
    { id: 'cost-4', name: 'Marcus Williams', avatar: 'MW', title: 'Operations Director', active_tasks: 10, availability_status: 'available', cognitive_load: 68 },
    { id: 'cost-5', name: 'Jessica Park', avatar: 'JP', title: 'Marketing Director', active_tasks: 14, availability_status: 'busy', cognitive_load: 82 },
    { id: 'cost-6', name: 'David Park', avatar: 'DP', title: 'Finance Director', active_tasks: 8, availability_status: 'available', cognitive_load: 58 },
    { id: 'cost-7', name: 'Maya Johnson', avatar: 'MJ', title: 'Soul Food - Chief of Staff', active_tasks: 8, availability_status: 'available', cognitive_load: 68 },
    { id: 'cost-8', name: 'Jordan Lee', avatar: 'JL', title: 'Soul Food - Partnerships', active_tasks: 6, availability_status: 'available', cognitive_load: 62 }
  ],
  // Burnout alerts for executive and senior leaders
  burnout_alerts: [
    { member_name: 'Alexandra Chen', risk_score: 72, role: 'CEO', trend: 'stable', days_to_critical: 14, note: 'Heavy decision load this quarter — recommend protecting recovery time' },
    { member_name: 'James Chen', risk_score: 88, role: 'West Regional Director', trend: 'increasing', days_to_critical: 3, note: 'Critical — needs immediate workload intervention' }
  ],
  // Complete executive review pipeline - this is the Chief of Staff's primary responsibility
  executive_reviews: [
    { id: 'coser-1', name: 'National Marketing Campaign Q2', type: 'Strategic Initiative', status: 'pending_review', submitted_by: 'Jessica Park', days_pending: 2, sla_status: 'warning' },
    { id: 'coser-2', name: 'Cross-Regional Resource Reallocation', type: 'Budget Request', status: 'pending_review', submitted_by: 'Marcus Williams', days_pending: 1, sla_status: 'on_track' },
    { id: 'coser-3', name: 'Soul Food Nike Partnership', type: 'Partnership Contract', status: 'pending_review', submitted_by: 'Jordan Lee', days_pending: 2, sla_status: 'warning' },
    { id: 'coser-4', name: 'Q2 Hiring Plan Approval', type: 'HR Request', status: 'pending_review', submitted_by: 'HR Director', days_pending: 3, sla_status: 'at_risk' },
    { id: 'coser-5', name: 'West Region Recovery Plan', type: 'Operational Plan', status: 'pending_review', submitted_by: 'James Chen', days_pending: 1, sla_status: 'on_track' }
  ],
  // Administrative and coordinative quick actions
  quick_actions: [
    { key: 'draft_communication', label: 'Draft Executive Communication', icon: 'MessageSquare' },
    { key: 'schedule_meeting', label: 'Schedule Meeting', icon: 'Calendar' },
    { key: 'escalate', label: 'Escalate to Executive', icon: 'TrendingUp' },
    { key: 'update_decision_log', label: 'Update Decision Log', icon: 'Clipboard' },
    { key: 'compile_report', label: 'Compile Report', icon: 'FileText' }
  ],
  // Chief of Staff specific flags
  isChiefOfStaff: true,
  showFullExecutiveReviews: true,
  showExecutiveBurnout: true
});

// Get data for a specific role
export const getRoleData = (roleId) => {
  switch (roleId) {
    case 'ceo':
      return getCEOData();
    case 'chief_of_staff':
      return getChiefOfStaffData();
    case 'national_director':
      return getNationalDirectorData();
    case 'regional_director':
      return getRegionalDirectorData();
    case 'unique_ip_director':
      return getUniqueIPDirectorData();
    case 'unique_ip_operations':
      return getUniqueIPOperationsData();
    case 'marketing_manager':
      return getMarketingManagerData();
    case 'team_member':
      return getTeamMemberData();
    default:
      return null;
  }
};

// ================================================
// PULSE PAGE DEMO DATA BY ROLE
// ================================================
export const getPulseRoleData = (roleId) => {
  const baseExecutionMomentum = {
    Strong: { label: 'Strong', statusColor: '#10B981' },
    Building: { label: 'Building', statusColor: '#F59E0B' },
    Steady: { label: 'Steady', statusColor: '#3B82F6' },
    'Focus Required': { label: 'Focus Required', statusColor: '#EF4444' }
  };

  switch (roleId) {
    case 'ceo':
      // Return demo data for CEO - fallback when API fails
      return {
        isCEO: true,
        scope_label: 'Organization Overview',
        execution_momentum: 'Building',
        velocity_this_week: 124,
        velocity_last_week: 121,
        on_time_phase_rate: 0.87,
        active_momentum: 124,
        needing_attention: 0,
        work_advancing_feed: [
          { id: 'ceo-1', label: 'Task Completed', title: 'Campaign brief draft', assignee: 'Marcus Rivera', time: 'Just now', project: 'Q2 Marketing Campaign' },
          { id: 'ceo-2', label: 'Phase Advanced', title: 'Design Phase', assignee: 'Design Team', time: '1 hour ago', project: 'Platform Evolution 2026' },
          { id: 'ceo-3', label: 'Review Submitted', title: 'Quarterly report', assignee: 'Sarah Chen', time: '2 hours ago', project: 'Q3 Growth Initiative' }
        ],
        lane_performance: [
          { lane: 'vision', lane_name: 'Vision & Expansion', color: '#8B5CF6', health: 'healthy', on_time_percentage: 0.92, active_count: 15, velocity_trend: 'up' },
          { lane: 'strategy', lane_name: 'Strategy & Architecture', color: '#3B82F6', health: 'healthy', on_time_percentage: 0.88, active_count: 12, velocity_trend: 'up' },
          { lane: 'relationships', lane_name: 'Relationships & Networks', color: '#10B981', health: 'healthy', on_time_percentage: 0.85, active_count: 8, velocity_trend: 'stable' },
          { lane: 'creative', lane_name: 'Creative & Content', color: '#F59E0B', health: 'elevated', on_time_percentage: 0.78, active_count: 18, velocity_trend: 'down' },
          { lane: 'execution', lane_name: 'Execution & Delivery', color: '#EF4444', health: 'healthy', on_time_percentage: 0.91, active_count: 22, velocity_trend: 'up' }
        ],
        project_spotlight: [
          { id: 'sp-1', name: 'Platform Evolution 2026', status: 'on-track', progress: 68, team_size: 8, days_to_target: 45 },
          { id: 'sp-2', name: 'Q3 Growth Acceleration', status: 'attention', progress: 45, team_size: 5, days_to_target: 30 }
        ]
      };
    
    case 'chief_of_staff':
      return {
        isChiefOfStaff: true,
        scope_label: 'Organization-Wide Coordination',
        
        // Coordination-focused momentum hero
        executiveMomentum: {
          headline: 'Organization is executing well',
          subheadline: '2 items need your attention today',
          supportingText: 'Executive review pipeline is current. One department showing velocity drop worth monitoring.',
          status: 'on-track'
        },
        
        // Chief of Staff specific KPIs
        coordinationKPIs: {
          executiveReviewPipeline: { value: 5, trend: -2, trendLabel: 'vs yesterday', label: 'Executive Review Pipeline' },
          itemsEscalated: { value: 3, label: 'Items Escalated', sublabel: 'This Week' },
          followUpsOutstanding: { value: 12, label: 'Follow-Ups Outstanding' },
          crossDeptBlockers: { value: 2, label: 'Cross-Dept Blockers', sublabel: 'Requiring coordination' }
        },
        
        // Work advancing feed - tagged with Company/Soul Food
        workAdvancingFeed: [
          { id: 'cospf-1', title: 'Soul Food partnership agreement advanced to signature stage', timestamp: '1 hour ago', type: 'completed', icon: 'CheckCircle2', entity: 'soulfood' },
          { id: 'cospf-2', title: 'Platform Evolution project phase completed', timestamp: '2 hours ago', type: 'completed', icon: 'Target', entity: 'company' },
          { id: 'cospf-3', title: 'Regional Director submitted weekly report on time', timestamp: '3 hours ago', type: 'submitted', icon: 'FileText', entity: 'company' },
          { id: 'cospf-4', title: 'Board presentation deck approved by executive', timestamp: '4 hours ago', type: 'approved', icon: 'CheckCircle2', entity: 'company' },
          { id: 'cospf-5', title: 'Soul Food community event logistics confirmed', timestamp: 'Yesterday', type: 'completed', icon: 'Calendar', entity: 'soulfood' },
          { id: 'cospf-6', title: 'Q2 hiring plan submitted for executive review', timestamp: 'Yesterday', type: 'submitted', icon: 'Users', entity: 'company' }
        ],
        
        // All lanes visible - Chief of Staff monitors entire organization
        lane_performance: COGNITIVE_LANES.map(lane => ({
          lane: lane.id,
          lane_name: lane.name,
          color: lane.color,
          health: Math.random() > 0.2 ? 'healthy' : Math.random() > 0.5 ? 'elevated' : 'attention',
          on_time_percentage: 0.72 + Math.random() * 0.23,
          active_count: Math.floor(Math.random() * 15) + 5,
          velocity_trend: Math.random() > 0.3 ? 'up' : 'down'
        })),
        
        // All projects across organization including Soul Food
        allProjects: [
          { id: 'cosp-1', name: 'Platform Evolution', status: 'on-track', progress: 68, entity: 'company', primaryLane: 'strategy' },
          { id: 'cosp-2', name: 'Q2 Marketing Campaign', status: 'attention', progress: 45, entity: 'company', primaryLane: 'marketing' },
          { id: 'cosp-3', name: 'Soul Food Summer Series', status: 'on-track', progress: 72, entity: 'soulfood', primaryLane: 'creative' },
          { id: 'cosp-4', name: 'Regional Expansion East', status: 'on-track', progress: 55, entity: 'company', primaryLane: 'operations' },
          { id: 'cosp-5', name: 'Soul Food Nike Partnership', status: 'critical', progress: 85, entity: 'soulfood', primaryLane: 'partnerships' },
          { id: 'cosp-6', name: 'Infrastructure Modernization', status: 'on-track', progress: 38, entity: 'company', primaryLane: 'execution' }
        ],
        
        // Detailed executive review backlog - primary responsibility
        executiveReviewBacklog: [
          { id: 'cosrb-1', name: 'National Marketing Campaign Q2', type: 'Strategic Initiative', submitted_by: 'Jessica Park', hours_pending: 42, sla_status: 'warning', content_summary: 'Q2 campaign budget approval for $850K regional media spend' },
          { id: 'cosrb-2', name: 'Cross-Regional Resource Reallocation', type: 'Budget Request', submitted_by: 'Marcus Williams', hours_pending: 18, sla_status: 'on_track', content_summary: 'Engineering resource shift from Central to West region' },
          { id: 'cosrb-3', name: 'Soul Food Nike Partnership', type: 'Partnership Contract', submitted_by: 'Jordan Lee', hours_pending: 38, sla_status: 'warning', content_summary: 'Nike collaboration terms requiring executive sign-off' },
          { id: 'cosrb-4', name: 'Q2 Hiring Plan Approval', type: 'HR Request', submitted_by: 'HR Director', hours_pending: 52, sla_status: 'at_risk', content_summary: '12 new hires across Marketing and Operations' },
          { id: 'cosrb-5', name: 'West Region Recovery Plan', type: 'Operational Plan', submitted_by: 'James Chen', hours_pending: 14, sla_status: 'on_track', content_summary: 'Intervention plan for velocity improvement' }
        ],
        
        // Soul Food Division Health mini-dashboard
        soulFoodDivisionHealth: {
          velocity: 28,
          velocityTrend: 'up',
          activeInitiatives: 6,
          itemsNeedingExecutiveAttention: 1,
          brandHealth: 87,
          upcomingEvents: 3
        }
      };
    
    case 'national_director':
      return {
        execution_momentum: 'Building',
        velocity_this_week: 142,
        velocity_last_week: 128,
        on_time_phase_rate: 0.82,
        active_momentum_count: 18,
        needing_attention_count: 4,
        level_focus: { l4_decisions: 8, operational_load_percentage: 72 },
        lane_performance: COGNITIVE_LANES.slice(0, 6).map(lane => ({
          lane: lane.id,
          on_time_percentage: 0.7 + Math.random() * 0.25,
          active_count: Math.floor(Math.random() * 12) + 4
        })),
        scope_label: 'National Organization',
        region_breakdown: [
          { name: 'East Region', velocity: 38, status: 'on-track' },
          { name: 'West Region', velocity: 32, status: 'attention' },
          { name: 'Central Region', velocity: 42, status: 'on-track' },
          { name: 'South Region', velocity: 30, status: 'attention' }
        ],
        review_backlog: { pending: 6, oldest_hours: 36, sla_at_risk: 2 }
      };
    
    case 'regional_director':
      return {
        execution_momentum: 'Steady',
        velocity_this_week: 38,
        velocity_last_week: 35,
        on_time_phase_rate: 0.78,
        active_momentum_count: 8,
        needing_attention_count: 2,
        level_focus: { l4_decisions: 2, operational_load_percentage: 68 },
        lane_performance: COGNITIVE_LANES.slice(0, 4).map(lane => ({
          lane: lane.id,
          on_time_percentage: 0.72 + Math.random() * 0.2,
          active_count: Math.floor(Math.random() * 8) + 2
        })),
        scope_label: 'East Region',
        team_breakdown: [
          { name: 'Marketing', capacity: 92, status: 'elevated' },
          { name: 'Operations', capacity: 68, status: 'optimal' },
          { name: 'Sales', capacity: 75, status: 'optimal' },
          { name: 'Finance', capacity: 55, status: 'optimal' }
        ],
        review_backlog: { pending: 3, oldest_hours: 18, sla_at_risk: 0 }
      };
    
    case 'unique_ip_director':
      return {
        execution_momentum: 'Strong',
        velocity_this_week: 28,
        velocity_last_week: 24,
        on_time_phase_rate: 0.88,
        active_momentum_count: 6,
        needing_attention_count: 1,
        level_focus: { l4_decisions: 3, operational_load_percentage: 58 },
        lane_performance: [
          { lane: 'creative', lane_name: 'Creative', on_time_percentage: 0.92, active_count: 8, color: '#A855F7' },
          { lane: 'partnerships', lane_name: 'Partnerships', on_time_percentage: 0.85, active_count: 4, color: '#F59E0B' },
          { lane: 'content', lane_name: 'Content', on_time_percentage: 0.78, active_count: 6, color: '#EC4899' },
          { lane: 'events', lane_name: 'Events', on_time_percentage: 0.95, active_count: 3, color: '#10B981' }
        ],
        scope_label: 'Soul Food Division',
        brand_metrics: {
          engagement_rate: 8.4,
          follower_growth: 12,
          content_output: 24,
          partnership_pipeline: 4
        },
        review_backlog: { pending: 2, oldest_hours: 12, sla_at_risk: 0 },
        isUniqueIP: true
      };
    
    case 'unique_ip_operations':
      return {
        // Personal momentum - operational execution
        isPersonalView: true,
        isUniqueIP: true,
        isOperations: true,
        hideExecutiveMetrics: true,
        hideReviewBacklog: true,
        scope_label: 'Soul Food Operations',
        
        // Personal hero card data
        personalMomentum: {
          headline: 'Operations running smoothly',
          subheadline: "today's setup completed on time",
          supportingText: 'All vendor deliveries confirmed and inventory logged. Equipment checks complete.',
          status: 'on-track'
        },
        
        // Personal KPIs (4 stat cards)
        personalKPIs: {
          tasksCompletedToday: { value: 5, target: 8, label: 'Tasks Completed Today' },
          shiftsCompletedMonth: { value: 12, label: 'Shifts Completed', sublabel: 'This Month' },
          onTimeOpsRate: { value: 94, trend: 3, trendLabel: 'vs last month', label: 'On-Time Ops Rate' },
          issuesReported: { value: 3, resolved: 3, label: 'Issues Reported', sublabel: 'All Resolved' }
        },
        
        // Personal work advancing feed (operational completions)
        personalWorkFeed: [
          { id: 'owf-1', title: 'Venue setup completed for Thursday event', timestamp: '2 hours ago', type: 'completed', icon: 'CheckCircle2' },
          { id: 'owf-2', title: 'Vendor delivery from Fresh Farms received and logged', timestamp: '3 hours ago', type: 'completed', icon: 'Package' },
          { id: 'owf-3', title: 'Equipment inventory reconciled — all items accounted', timestamp: '4 hours ago', type: 'completed', icon: 'Clipboard' },
          { id: 'owf-4', title: 'Pre-event checklist submitted to Operations Lead', timestamp: '5 hours ago', type: 'completed', icon: 'FileCheck' },
          { id: 'owf-5', title: 'Signage placement confirmed with venue coordinator', timestamp: 'Yesterday', type: 'completed', icon: 'MapPin' }
        ],
        
        // My Operations Performance (replaces Lane Performance)
        myOperationsPerformance: {
          thisWeek: { tasksCompleted: 18, eventsSupported: 2, issuesResolved: 2 },
          lastWeek: { tasksCompleted: 15, eventsSupported: 2, issuesResolved: 1 },
          trend: 'up',
          reliabilityScore: 94
        },
        
        // Today's Priorities (operational tasks with times)
        todaysPriorities: [
          { id: 'tp-1', title: 'Midday station restock', time: '12:00 PM', status: 'upcoming', priority: 'high' },
          { id: 'tp-2', title: 'Event support rotation begins', time: '2:00 PM', status: 'upcoming', priority: 'critical' },
          { id: 'tp-3', title: 'Post-event breakdown coordination', time: '6:00 PM', status: 'planned', priority: 'high' }
        ],
        
        // Upcoming Events (replaces Executive Review)
        upcomingEvents: [
          { id: 'ue-1', name: 'Soul Food Community Dinner', date: 'Thursday, Feb 22', location: 'Downtown Community Center', myRole: 'Setup & Coordination Lead' },
          { id: 'ue-2', name: 'Pop-up Market Activation', date: 'Saturday, Feb 24', location: 'Riverside Plaza', myRole: 'Vendor Logistics Support' },
          { id: 'ue-3', name: 'Monthly Team Appreciation', date: 'Tuesday, Feb 27', location: 'Soul Food HQ', myRole: 'Event Support' }
        ],
        
        // Shift Log (personal operational history)
        shiftLog: [
          { id: 'sl-1', date: 'Feb 19', eventName: 'Soul Food Launch Party', status: 'complete', hours: 6 },
          { id: 'sl-2', date: 'Feb 17', eventName: 'Weekend Pop-up Market', status: 'complete', hours: 8 },
          { id: 'sl-3', date: 'Feb 15', eventName: 'Community Dinner Service', status: 'complete', hours: 5 },
          { id: 'sl-4', date: 'Feb 12', eventName: 'Vendor Setup Day', status: 'complete', hours: 4 },
          { id: 'sl-5', date: 'Feb 10', eventName: 'Inventory Count', status: 'complete', hours: 3 }
        ]
      };
    
    case 'marketing_manager':
      return {
        execution_momentum: 'Building',
        velocity_this_week: 18,
        velocity_last_week: 16,
        on_time_phase_rate: 0.75,
        active_momentum_count: 5,
        needing_attention_count: 2,
        level_focus: { l4_decisions: 0, operational_load_percentage: 78 },
        lane_performance: [
          { lane: 'content', lane_name: 'Content', on_time_percentage: 0.82, active_count: 6, color: '#FB923C' },
          { lane: 'campaigns', lane_name: 'Campaigns', on_time_percentage: 0.68, active_count: 4, color: '#8B5CF6' },
          { lane: 'social', lane_name: 'Social', on_time_percentage: 0.88, active_count: 8, color: '#3B82F6' }
        ],
        scope_label: 'Marketing Department',
        campaign_metrics: {
          active_campaigns: 3,
          pending_approvals: 5,
          content_queue: 12,
          ctr_avg: 3.2
        },
        review_backlog: { pending: 1, oldest_hours: 8, sla_at_risk: 0 }
      };
    
    case 'team_member':
      return {
        // Personal view flags
        isPersonalView: true,
        hideExecutiveMetrics: true,
        hideReviewBacklog: true,
        scope_label: 'My Work',
        
        // Personal hero card data
        personalMomentum: {
          headline: "You're on track today",
          subheadline: '3 of 8 tasks completed',
          supportingText: 'Your on-time delivery rate is up 12% from last week. Keep up the momentum!',
          status: 'on-track'
        },
        
        // Personal KPIs (4 stat cards)
        personalKPIs: {
          tasksCompletedToday: { value: 3, target: 8, label: 'My Tasks Today', progress: 38 },
          onTimeRate: { value: 87, trend: 12, trendLabel: 'vs last week', label: 'My On-Time Rate' },
          currentLoad: { value: 68, status: 'healthy', label: 'My Current Load' }, // healthy, elevated, high
          streak: { value: 5, label: 'Day Streak', sublabel: 'All tasks completed' }
        },
        
        // Personal work advancing feed (my completions only)
        personalWorkFeed: [
          { id: 'pwf-1', title: 'Campaign asset resizing completed', timestamp: '1 hour ago', type: 'completed', icon: 'Image' },
          { id: 'pwf-2', title: 'Instagram caption set submitted for review', timestamp: '2 hours ago', type: 'submitted', icon: 'Send' },
          { id: 'pwf-3', title: 'Brand guidelines document reviewed', timestamp: '3 hours ago', type: 'completed', icon: 'FileCheck' },
          { id: 'pwf-4', title: 'Weekly content report drafted', timestamp: 'Yesterday', type: 'completed', icon: 'FileText' },
          { id: 'pwf-5', title: 'Creative brief feedback incorporated', timestamp: 'Yesterday', type: 'completed', icon: 'CheckCircle2' }
        ],
        
        // My Lane Performance (single lane view)
        myLanePerformance: {
          lane: 'Marketing',
          laneColor: '#FB923C',
          laneHealth: 82,
          myContribution: 14, // tasks I've completed in this lane this week
          laneVelocity: 45,
          myVsLaneAvg: 'above', // above, on-par, below
          assignmentsStatus: 'ahead' // ahead, on-track, behind
        },
        
        // Today's Priorities (top 3 urgent tasks)
        todaysPriorities: [
          { id: 'tp-1', title: 'Complete Instagram caption set', dueTime: '2:00 PM', status: 'in-progress', priority: 'high' },
          { id: 'tp-2', title: 'Address feedback on campaign visuals', dueTime: '4:00 PM', status: 'not-started', priority: 'high' },
          { id: 'tp-3', title: 'Submit weekly content report', dueTime: '5:00 PM', status: 'not-started', priority: 'medium' }
        ],
        
        // Personal Wins (last 5 completed tasks)
        personalWins: [
          { id: 'pw-1', title: 'Campaign asset resizing', completedAt: 'Today, 10:30 AM' },
          { id: 'pw-2', title: 'Brand guidelines review', completedAt: 'Today, 9:15 AM' },
          { id: 'pw-3', title: 'Social media copy for March batch', completedAt: 'Yesterday, 4:45 PM' },
          { id: 'pw-4', title: 'Newsletter asset preparation', completedAt: 'Yesterday, 2:30 PM' },
          { id: 'pw-5', title: 'Content performance summary', completedAt: 'Feb 18, 5:00 PM' }
        ],
        
        // My Upcoming Deadlines (next 7 days - replaces Executive Review)
        myUpcomingDeadlines: [
          { id: 'ud-1', title: 'Instagram caption set', dueDate: 'Today', priority: 'high' },
          { id: 'ud-2', title: 'Campaign feedback response', dueDate: 'Today', priority: 'high' },
          { id: 'ud-3', title: 'Weekly content report', dueDate: 'Today', priority: 'medium' },
          { id: 'ud-4', title: 'Blog post outlines (3)', dueDate: 'Tomorrow', priority: 'medium' },
          { id: 'ud-5', title: 'Email newsletter assets', dueDate: 'Wed, Feb 22', priority: 'medium' },
          { id: 'ud-6', title: 'Team sync presentation', dueDate: 'Thu, Feb 23', priority: 'low' },
          { id: 'ud-7', title: 'March campaign captions batch 1', dueDate: 'Mon, Feb 27', priority: 'high' }
        ]
      };
    
    default:
      return null;
  }
};

// ================================================
// INSIGHTS PAGE DEMO DATA BY ROLE
// ================================================
export const getInsightsRoleData = (roleId) => {
  switch (roleId) {
    case 'ceo':
      return null; // Uses API data
    
    case 'chief_of_staff':
      return {
        isChiefOfStaff: true,
        scope_label: 'Organization-Wide Intelligence',
        
        // Comprehensive organizational health - broadest view
        health: {
          overall_score: 78,
          trend: 'up',
          trend_change: 3,
          execution_score: 80,
          team_health_score: 74,
          delivery_score: 82,
          collaboration_score: 76
        },
        
        // Chief of Staff Dashboard - coordination metrics
        chiefOfStaffDashboard: {
          itemsRequiringFollowUp: { value: 12, trend: -3, label: 'Items Requiring Follow-Up' },
          decisionsMadeThisWeek: { value: 18, trend: 4, label: 'Decisions Made This Week' },
          crossDeptEscalations: { value: 4, trend: -2, label: 'Cross-Dept Escalations' },
          executiveTimeProtected: { value: 78, unit: '%', label: 'Executive Time Protected', sublabel: 'Meetings filtered this week' }
        },
        
        // Full cognitive load across all departments
        cognitive_load: {
          overall_load: 72,
          load_status: 'elevated',
          l4_strategic_load: 18,
          l3_improvement_load: 24,
          l2_operational_load: 34,
          l1_execution_load: 24,
          // Per-department breakdown
          departmentLoads: [
            { name: 'Operations', load: 75, status: 'elevated' },
            { name: 'Marketing', load: 82, status: 'high' },
            { name: 'Finance', load: 58, status: 'healthy' },
            { name: 'Soul Food', load: 68, status: 'elevated' },
            { name: 'Engineering', load: 71, status: 'elevated' }
          ]
        },
        
        // Hotspots with "Priority for Executive Attention" filter
        hotspots: [
          { name: 'James Chen', avatar: 'JC', role: 'West Regional Director', score: 88, level: 'critical', active_tasks: 15, lane_switches: 8, complexity: 82, priorityForExecutive: true },
          { name: 'Jessica Park', avatar: 'JP', role: 'Marketing Director', score: 82, level: 'high', active_tasks: 14, lane_switches: 6, complexity: 78, priorityForExecutive: false },
          { name: 'Elena Rodriguez', avatar: 'ER', role: 'South Regional Director', score: 78, level: 'high', active_tasks: 12, lane_switches: 5, complexity: 75, priorityForExecutive: false },
          { name: 'David Kim', avatar: 'DK', role: 'Engineering Lead', score: 76, level: 'elevated', active_tasks: 11, lane_switches: 4, complexity: 72, priorityForExecutive: false }
        ],
        
        // All projects with Executive Visibility column
        allProjects: [
          { id: 'ip-1', name: 'Platform Evolution', status: 'on-track', progress: 68, entity: 'company', primaryLane: 'Strategy', executiveBriefed: true, lastBriefed: '2 days ago' },
          { id: 'ip-2', name: 'Q2 Marketing Campaign', status: 'attention', progress: 45, entity: 'company', primaryLane: 'Marketing', executiveBriefed: false, lastBriefed: '8 days ago', overdue: true },
          { id: 'ip-3', name: 'Soul Food Summer Series', status: 'on-track', progress: 72, entity: 'soulfood', primaryLane: 'Creative', executiveBriefed: true, lastBriefed: '1 day ago' },
          { id: 'ip-4', name: 'Regional Expansion East', status: 'on-track', progress: 55, entity: 'company', primaryLane: 'Operations', executiveBriefed: true, lastBriefed: '3 days ago' },
          { id: 'ip-5', name: 'Soul Food Nike Partnership', status: 'critical', progress: 85, entity: 'soulfood', primaryLane: 'Partnerships', executiveBriefed: true, lastBriefed: 'Today' },
          { id: 'ip-6', name: 'Infrastructure Modernization', status: 'on-track', progress: 38, entity: 'company', primaryLane: 'Execution', executiveBriefed: false, lastBriefed: '12 days ago', overdue: true }
        ],
        
        // Coordination impact trends - Chief of Staff specific
        coordinationImpactTrends: {
          executiveReviewTurnaround: { current: 18, previous: 24, unit: 'hours', trend: 'down', isGood: true, label: 'Executive Review Turnaround' },
          crossDeptEscalations: { current: 4, previous: 7, trend: 'down', isGood: true, label: 'Cross-Dept Escalations' },
          decisionBacklog: { current: 5, previous: 8, trend: 'down', isGood: true, label: 'Decision Backlog' },
          meetingsFiltered: { current: 12, previous: 8, trend: 'up', isGood: true, label: 'Meetings Filtered for Executive' },
          weeklyData: [
            { week: 'Week 1', turnaround: 28, escalations: 9, backlog: 12 },
            { week: 'Week 2', turnaround: 26, escalations: 8, backlog: 10 },
            { week: 'Week 3', turnaround: 24, escalations: 7, backlog: 9 },
            { week: 'Week 4', turnaround: 22, escalations: 6, backlog: 8 },
            { week: 'Week 5', turnaround: 22, escalations: 6, backlog: 7 },
            { week: 'Week 6', turnaround: 20, escalations: 5, backlog: 6 },
            { week: 'Week 7', turnaround: 19, escalations: 5, backlog: 5 },
            { week: 'Week 8', turnaround: 18, escalations: 4, backlog: 5 }
          ]
        },
        
        // All team members with "Needs Chief of Staff Attention" filter
        allTeamMembers: [
          { id: 'tm-1', name: 'Alexandra Chen', avatar: 'AC', role: 'CEO', department: 'Executive', burnoutRisk: 72, overdueItems: 0, flaggedForFollowup: false, needsAttention: false },
          { id: 'tm-2', name: 'James Chen', avatar: 'JC', role: 'West Regional Director', department: 'Operations', burnoutRisk: 88, overdueItems: 3, flaggedForFollowup: true, needsAttention: true },
          { id: 'tm-3', name: 'Sarah Martinez', avatar: 'SM', role: 'East Regional Director', department: 'Operations', burnoutRisk: 65, overdueItems: 0, flaggedForFollowup: false, needsAttention: false },
          { id: 'tm-4', name: 'Jessica Park', avatar: 'JP', role: 'Marketing Director', department: 'Marketing', burnoutRisk: 76, overdueItems: 2, flaggedForFollowup: true, needsAttention: true },
          { id: 'tm-5', name: 'Marcus Williams', avatar: 'MW', role: 'Operations Director', department: 'Operations', burnoutRisk: 58, overdueItems: 1, flaggedForFollowup: false, needsAttention: false },
          { id: 'tm-6', name: 'Jordan Lee', avatar: 'JL', role: 'Soul Food - Partnerships', department: 'Soul Food', burnoutRisk: 52, overdueItems: 0, flaggedForFollowup: false, needsAttention: false },
          { id: 'tm-7', name: 'Maya Johnson', avatar: 'MJ', role: 'Soul Food - Chief of Staff', department: 'Soul Food', burnoutRisk: 68, overdueItems: 0, flaggedForFollowup: true, needsAttention: false },
          { id: 'tm-8', name: 'David Park', avatar: 'DP', role: 'Finance Director', department: 'Finance', burnoutRisk: 48, overdueItems: 0, flaggedForFollowup: false, needsAttention: false }
        ],
        
        // All lanes with health scores
        lane_performance: COGNITIVE_LANES.map((lane, idx) => ({
          lane: lane.id,
          name: lane.name,
          color: lane.color,
          health_score: 70 + Math.floor(Math.random() * 25),
          on_time_percentage: 0.72 + Math.random() * 0.23,
          active_count: Math.floor(Math.random() * 15) + 5,
          velocity_trend: idx % 3 === 0 ? 'down' : 'up'
        })),
        
        // Complete executive review pipeline with full details
        executiveReviewPipeline: [
          { id: 'erp-1', name: 'National Marketing Campaign Q2', type: 'Strategic Initiative', submitted_by: 'Jessica Park', hours_pending: 42, sla_countdown: '6h remaining', sla_status: 'warning', content_summary: 'Q2 campaign budget approval for $850K' },
          { id: 'erp-2', name: 'Cross-Regional Resource Reallocation', type: 'Budget Request', submitted_by: 'Marcus Williams', hours_pending: 18, sla_countdown: '30h remaining', sla_status: 'on_track', content_summary: 'Engineering resource shift to West region' },
          { id: 'erp-3', name: 'Soul Food Nike Partnership', type: 'Partnership Contract', submitted_by: 'Jordan Lee', hours_pending: 38, sla_countdown: '10h remaining', sla_status: 'warning', content_summary: 'Nike collaboration terms' },
          { id: 'erp-4', name: 'Q2 Hiring Plan', type: 'HR Request', submitted_by: 'HR Director', hours_pending: 52, sla_countdown: '-4h overdue', sla_status: 'at_risk', content_summary: '12 new hires across departments' },
          { id: 'erp-5', name: 'West Region Recovery Plan', type: 'Operational Plan', submitted_by: 'James Chen', hours_pending: 14, sla_countdown: '34h remaining', sla_status: 'on_track', content_summary: 'Velocity improvement intervention' }
        ],
        
        // Unique Coordination Intelligence tab data
        coordinationIntelligence: {
          avgExecutiveResponseTime: { value: 4.2, unit: 'hours', trend: 'down', isGood: true },
          escalationSources: [
            { department: 'Marketing', count: 8, percentage: 32 },
            { department: 'Operations', count: 6, percentage: 24 },
            { department: 'Engineering', count: 5, percentage: 20 },
            { department: 'Soul Food', count: 3, percentage: 12 },
            { department: 'Finance', count: 3, percentage: 12 }
          ],
          decisionBacklogTrend: [
            { month: 'Oct', backlog: 12 },
            { month: 'Nov', backlog: 10 },
            { month: 'Dec', backlog: 8 },
            { month: 'Jan', backlog: 7 },
            { month: 'Feb', backlog: 5 }
          ],
          calendarHeatmap: {
            monday: { demand: 82, label: 'High' },
            tuesday: { demand: 68, label: 'Moderate' },
            wednesday: { demand: 75, label: 'Moderate' },
            thursday: { demand: 58, label: 'Low' },
            friday: { demand: 92, label: 'Very High' }
          }
        },
        
        // Key metrics visible
        key_metrics: [
          { name: 'Org Velocity', value: 156, unit: 'number', trend: 'up', trend_value: 12 },
          { name: 'Executive Decisions', value: 18, unit: 'number', trend: 'up', trend_value: 3, label: 'This Week' },
          { name: 'Review Turnaround', value: 18, unit: 'hours', trend: 'down', trend_value: -6, isGood: true },
          { name: 'Escalation Rate', value: 8, unit: 'percentage', trend: 'down', trend_value: -4, isGood: true }
        ],
        
        // Soul Food health alongside company
        soulFoodHealth: {
          healthScore: 82,
          velocity: 28,
          activeProjects: 6,
          burnoutRiskAvg: 58
        },
        
        // Risks visible to Chief of Staff
        risks: [
          { title: 'West Region Director burnout risk critical', severity: 'critical', description: 'James Chen at 88% burnout risk', recommended_action: 'Escalate to executive for intervention' },
          { title: 'Marketing executive briefing overdue', severity: 'high', description: 'Q2 Campaign not briefed in 8 days', recommended_action: 'Schedule executive update' },
          { title: 'Cross-regional resource conflict', severity: 'medium', description: 'Engineering resources contested', recommended_action: 'Coordinate resolution meeting' }
        ]
      };
    
    case 'national_director':
      return {
        health: {
          overall_score: 76,
          trend: 'up',
          trend_change: 4,
          execution_score: 78,
          team_health_score: 72,
          delivery_score: 80,
          collaboration_score: 74
        },
        cognitive_load: {
          overall_load: 68,
          load_status: 'elevated',
          l4_strategic_load: 22,
          l3_improvement_load: 28,
          l2_operational_load: 32,
          l1_execution_load: 18
        },
        scope_label: 'National Organization',
        risks: [
          { title: 'West Region falling behind targets', severity: 'high', description: '3 projects at risk in West Region', recommended_action: 'Schedule regional sync' },
          { title: 'Cross-regional resource conflict', severity: 'medium', description: 'Engineering resources contested between regions', recommended_action: 'Arbitrate allocation' },
          { title: 'Q2 budget variance increasing', severity: 'medium', description: '8% variance against planned spend', recommended_action: 'Review with finance' }
        ],
        hotspots: [
          { name: 'James Chen', avatar: 'JC', role: 'West Regional Director', score: 88, level: 'critical', active_tasks: 15, lane_switches: 8, complexity: 82 },
          { name: 'Elena Rodriguez', avatar: 'ER', role: 'South Regional Director', score: 82, level: 'high', active_tasks: 12, lane_switches: 6, complexity: 78 }
        ],
        key_metrics: [
          { name: 'National Velocity', value: 142, unit: 'number', trend: 'up', trend_value: 14 },
          { name: 'On-Time Delivery', value: 82, unit: 'percentage', trend: 'up', trend_value: 4 },
          { name: 'Team Utilization', value: 76, unit: 'percentage', trend: 'down', trend_value: -2 },
          { name: 'Phase Completion', value: 88, unit: 'percentage', trend: 'up', trend_value: 6 }
        ]
      };
    
    case 'regional_director':
      return {
        health: {
          overall_score: 72,
          trend: 'stable',
          trend_change: 1,
          execution_score: 74,
          team_health_score: 68,
          delivery_score: 76,
          collaboration_score: 70
        },
        cognitive_load: {
          overall_load: 72,
          load_status: 'elevated',
          l4_strategic_load: 15,
          l3_improvement_load: 25,
          l2_operational_load: 38,
          l1_execution_load: 22
        },
        scope_label: 'East Region',
        risks: [
          { title: 'Marketing team at capacity', severity: 'high', description: 'Marketing running at 95% utilization', recommended_action: 'Redistribute or defer work' },
          { title: '2 phases overdue this week', severity: 'medium', description: 'Deliverables slipping in Operations', recommended_action: 'Review with team leads' }
        ],
        hotspots: [
          { name: 'Jessica Park', avatar: 'JP', role: 'Marketing Manager', score: 82, level: 'high', active_tasks: 14, lane_switches: 6, complexity: 78 },
          { name: 'Carlos Rivera', avatar: 'CR', role: 'Creative Lead', score: 78, level: 'elevated', active_tasks: 10, lane_switches: 5, complexity: 72 }
        ],
        key_metrics: [
          { name: 'Regional Velocity', value: 38, unit: 'number', trend: 'up', trend_value: 3 },
          { name: 'On-Time Delivery', value: 78, unit: 'percentage', trend: 'stable', trend_value: 0 },
          { name: 'Team Utilization', value: 82, unit: 'percentage', trend: 'up', trend_value: 5 }
        ]
      };
    
    case 'unique_ip_director':
      return {
        health: {
          overall_score: 84,
          trend: 'up',
          trend_change: 6,
          execution_score: 86,
          team_health_score: 78,
          delivery_score: 88,
          collaboration_score: 84
        },
        cognitive_load: {
          overall_load: 62,
          load_status: 'optimal',
          l4_strategic_load: 28,
          l3_improvement_load: 32,
          l2_operational_load: 25,
          l1_execution_load: 15
        },
        scope_label: 'Soul Food Division',
        risks: [
          { title: 'Creative Lead showing burnout signs', severity: 'high', description: 'Kai at 85% cognitive load with declining output', recommended_action: 'Immediate check-in' },
          { title: 'Nike partnership deadline approaching', severity: 'medium', description: '48 hours to respond', recommended_action: 'Finalize decision' }
        ],
        hotspots: [
          { name: 'Kai Williams', avatar: 'KW', role: 'Creative Director', score: 85, level: 'high', active_tasks: 12, lane_switches: 7, complexity: 85 }
        ],
        brand_analytics: {
          engagement_growth: 24,
          audience_reach: '2.4M',
          content_performance: 92,
          partnership_value: '$1.2M'
        },
        key_metrics: [
          { name: 'Brand Health', value: 87, unit: 'percentage', trend: 'up', trend_value: 5 },
          { name: 'Content Output', value: 24, unit: 'number', trend: 'up', trend_value: 8 },
          { name: 'Partnership Pipeline', value: 4, unit: 'number', trend: 'stable', trend_value: 0 }
        ],
        isUniqueIP: true
      };
    
    case 'unique_ip_operations':
      return {
        // Personal view flags
        isPersonalView: true,
        isUniqueIP: true,
        isOperations: true,
        hideStrategicMetrics: true,
        hideTeamTab: true,
        hideReviewsTab: true,
        scope_label: 'Soul Food Operations',
        
        // Personal Overview metrics
        health: {
          overall_score: 94,
          trend: 'up',
          trend_change: 2,
          label: 'My Operations Score'
        },
        myPerformanceOverview: {
          operationsScore: 94,
          tasksOnTime: 96,
          eventsSupported: 4,
          issuesResolved: 3,
          summaryStatement: 'You have supported 4 Soul Food events this month with zero setup delays. Your operational reliability score is 94%.',
          loadTrendChart: [58, 62, 65, 68, 72, 70, 65, 62] // 8 weeks
        },
        
        // My Operational Patterns (replaces Hotspots)
        myOperationalPatterns: {
          complexDays: ['Thursday', 'Saturday'],
          complexReason: 'Major events and pop-ups cluster on these days',
          effortTasks: ['Multi-vendor coordination', 'Large event setups', 'Rush inventory reconciliation'],
          recommendation: 'Thursday and Saturday events require the most coordination effort — consider doing prep work the day before to reduce same-day complexity.',
          weeklyPattern: [
            { day: 'Mon', load: 45, label: 'Light' },
            { day: 'Tue', load: 55, label: 'Moderate' },
            { day: 'Wed', load: 62, label: 'Moderate' },
            { day: 'Thu', load: 85, label: 'Heavy' },
            { day: 'Fri', load: 50, label: 'Light' },
            { day: 'Sat', load: 88, label: 'Heavy' }
          ]
        },
        
        // My Projects (Soul Food operational projects)
        myProjects: [
          { id: 'op-1', name: 'Soul Food Community Dinners', myTasks: 12, totalTasks: 28, myOnTimeRate: 95, projectHealth: 88, role: 'Setup Coordinator' },
          { id: 'op-2', name: 'Pop-up Market Series', myTasks: 8, totalTasks: 35, myOnTimeRate: 100, projectHealth: 82, role: 'Logistics Support' },
          { id: 'op-3', name: 'Storefront Operations', myTasks: 6, totalTasks: 20, myOnTimeRate: 92, projectHealth: 85, role: 'Inventory & Display' }
        ],
        
        // Personal Operational Trends (8-week charts)
        myTrends: {
          eventsSupported: {
            label: 'Events Supported',
            data: [2, 3, 2, 4, 3, 4, 3, 4],
            insight: 'Your event support consistency has been strong over 8 weeks. Averaging 3+ events per week.'
          },
          tasksCompleted: {
            label: 'Tasks Completed',
            data: [14, 16, 15, 18, 20, 18, 17, 18],
            insight: 'Your task completion volume has grown steadily — up 29% from week 1.'
          },
          onTimeOps: {
            label: 'On-Time Ops Rate',
            data: [88, 90, 91, 94, 95, 94, 95, 96],
            insight: 'Your on-time operational rate has improved consistently — now at 96%, up from 88%.'
          },
          issuesFlagged: {
            label: 'Issues Flagged',
            data: [1, 0, 2, 4, 2, 1, 2, 1],
            insight: 'Issue flagging peaked in week 4 during the large community dinner — this is a positive signal showing operational awareness.'
          }
        },
        
        // My Lane view (Operations lane within Soul Food)
        myLane: {
          name: 'Soul Food Operations',
          color: '#F59E0B',
          laneHealth: 88,
          myContribution: 18,
          laneAvgContribution: 14,
          vsAverage: 'above',
          reliabilityScore: 94,
          laneAvgReliability: 86,
          supportiveMessage: "Your operational reliability is 8% above the team average — you're a key contributor to Soul Food's smooth execution."
        }
      };
    
    case 'marketing_manager':
      return {
        health: {
          overall_score: 70,
          trend: 'down',
          trend_change: -3,
          execution_score: 68,
          team_health_score: 65,
          delivery_score: 72,
          collaboration_score: 75
        },
        cognitive_load: {
          overall_load: 78,
          load_status: 'elevated',
          l4_strategic_load: 8,
          l3_improvement_load: 22,
          l2_operational_load: 45,
          l1_execution_load: 25
        },
        scope_label: 'Marketing Department',
        risks: [
          { title: 'Content queue backing up', severity: 'medium', description: '12 items awaiting review', recommended_action: 'Clear approval backlog' },
          { title: 'Campaign CTR below target', severity: 'medium', description: '15% below benchmark', recommended_action: 'Review creative approach' }
        ],
        hotspots: [
          { name: 'Casey Quinn', avatar: 'CQ', role: 'Social Media Coordinator', score: 80, level: 'high', active_tasks: 12, lane_switches: 5, complexity: 68 }
        ],
        campaign_insights: {
          active_campaigns: 3,
          avg_engagement: 4.2,
          content_in_queue: 12,
          approval_pending: 5
        },
        key_metrics: [
          { name: 'Campaign ROI', value: 3.2, unit: 'multiplier', trend: 'down', trend_value: -0.4 },
          { name: 'Content Velocity', value: 18, unit: 'number', trend: 'up', trend_value: 2 },
          { name: 'Team Capacity', value: 85, unit: 'percentage', trend: 'up', trend_value: 8 }
        ]
      };
    
    case 'team_member':
      return {
        // Personal view flags
        isPersonalView: true,
        hideStrategicMetrics: true,
        hideOrgMetrics: true,
        hideTeamTab: true,
        hideReviewsTab: true,
        scope_label: 'My Performance',
        
        // Personal Overview metrics
        health: {
          overall_score: 87,
          trend: 'up',
          trend_change: 3,
          label: 'My Performance Score'
        },
        myPerformanceOverview: {
          performanceScore: 87,
          completionRate: 89,
          loadTrend: 'stable',
          laneAlignment: 91,
          summaryStatement: 'You are operating at 91% lane alignment this month. Your on-time rate has improved 3 weeks in a row.',
          loadTrendChart: [62, 65, 68, 72, 70, 68, 65, 68] // 8 weeks of load scores
        },
        
        // My Cognitive Patterns (replaces Hotspots)
        myCognitivePatterns: {
          peakDays: ['Tuesday', 'Wednesday'],
          peakReason: 'Campaign deadlines and team syncs cluster mid-week',
          strainTasks: ['Multi-asset campaigns', 'Cross-team coordination', 'Rush content requests'],
          recommendation: 'Tuesdays and Wednesdays are your highest load days — consider scheduling simpler tasks on those afternoons to allow cognitive recovery.',
          weeklyPattern: [
            { day: 'Mon', load: 55, label: 'Light' },
            { day: 'Tue', load: 82, label: 'Heavy' },
            { day: 'Wed', load: 78, label: 'Heavy' },
            { day: 'Thu', load: 65, label: 'Moderate' },
            { day: 'Fri', load: 48, label: 'Light' }
          ]
        },
        
        // My Projects (only assigned projects with personal contribution)
        myProjects: [
          { id: 'mp-1', name: 'March Content Campaign', myTasks: 8, totalTasks: 24, myOnTimeRate: 88, projectHealth: 76, role: 'Content Creator' },
          { id: 'mp-2', name: 'Brand Refresh Initiative', myTasks: 3, totalTasks: 45, myOnTimeRate: 100, projectHealth: 82, role: 'Asset Support' },
          { id: 'mp-3', name: 'Social Media Optimization', myTasks: 5, totalTasks: 18, myOnTimeRate: 80, projectHealth: 71, role: 'Caption Writer' }
        ],
        
        // Personal Trends (8-week charts)
        myTrends: {
          taskVelocity: {
            label: 'My Task Velocity',
            data: [12, 14, 11, 16, 18, 15, 14, 15],
            insight: 'Your task velocity peaked in week 5 and has remained consistent. Strong steady output.'
          },
          onTimeRate: {
            label: 'My On-Time Rate',
            data: [82, 84, 85, 87, 89, 88, 89, 89],
            insight: 'Your on-time delivery has improved steadily over 8 weeks — up 7% overall.'
          },
          loadScore: {
            label: 'My Load Score',
            data: [62, 65, 68, 72, 78, 70, 68, 68],
            insight: 'Your load score spiked in week 5 — this coincided with the campaign launch period. It has since stabilized.'
          },
          laneAlignment: {
            label: 'My Lane Alignment',
            data: [85, 86, 88, 89, 90, 91, 91, 91],
            insight: 'You are increasingly focused within your Marketing lane — excellent alignment trajectory.'
          }
        },
        
        // My Lane view (single lane - Marketing)
        myLane: {
          name: 'Marketing',
          color: '#FB923C',
          laneHealth: 78,
          myContribution: 14,
          laneAvgContribution: 11,
          vsAverage: 'above', // above, on-par, below
          contributionPercentile: 72, // "You're in the top 28% of contributors in this lane"
          myOnTimeInLane: 89,
          laneAvgOnTime: 82,
          supportiveMessage: "You're performing above the lane average — your consistent delivery is helping the Marketing lane stay healthy."
        }
      };
    
    default:
      return null;
  }
};

// ================================================
// TASKS PAGE DEMO DATA BY ROLE
// ================================================
export const getTasksRoleData = (roleId) => {
  switch (roleId) {
    case 'ceo':
      return null; // Uses API data
    
    case 'chief_of_staff':
      return {
        isChiefOfStaff: true,
        scope_label: 'Organization-Wide Tasks',
        hasDualEntityFilter: true, // Unique to Chief of Staff
        tasks: [
          // Due Today - Company (navy border) 
          { id: 'cos-t1', title: 'Compile executive daily briefing document', status: 'Active', priority: 'critical', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date().toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t2', title: "Confirm tomorrow's board preparation materials are complete", status: 'Active', priority: 'high', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date().toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t3', title: 'Follow up with Regional Director on overdue weekly report', status: 'Active', priority: 'high', primary_lane: 'relationships', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date().toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          // Due Today - Soul Food (amber border)
          { id: 'cos-t4', title: 'Send partnership agreement to Soul Food Unique IP Director for review', status: 'Active', priority: 'high', primary_lane: 'relationships', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date().toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'soulfood' },
          { id: 'cos-t5', title: "Update organizational decision log with yesterday's executive decisions", status: 'Completed', priority: 'medium', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date().toISOString(), phase_count: 1, completed_phase_count: 1, entity: 'company' },
          { id: 'cos-t6', title: 'Confirm catering for Soul Food community event on Saturday', status: 'Active', priority: 'high', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date().toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'soulfood' },
          
          // Due This Week - Company
          { id: 'cos-t7', title: 'Prepare executive briefing deck for Friday leadership review', status: 'Planned', priority: 'critical', primary_lane: 'strategy', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 3*24*60*60*1000).toISOString(), phase_count: 2, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t8', title: 'Coordinate cross-department heads sync for Wednesday', status: 'Active', priority: 'high', primary_lane: 'relationships', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 1*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t9', title: 'Draft executive communication to board regarding Q1 performance', status: 'Planned', priority: 'high', primary_lane: 'relationships', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 2*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          // Due This Week - Soul Food
          { id: 'cos-t10', title: 'Review and organize Soul Food partnership pipeline documents', status: 'Planned', priority: 'high', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 2*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'soulfood' },
          { id: 'cos-t11', title: 'Schedule executive one-on-ones with all department heads for next month', status: 'Planned', priority: 'medium', primary_lane: 'relationships', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 4*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t12', title: 'Compile Soul Food monthly performance summary for executive review', status: 'Planned', priority: 'high', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 4*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'soulfood' },
          { id: 'cos-t13', title: 'Research venue options for Q2 executive offsite', status: 'Planned', priority: 'medium', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 5*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t14', title: 'Ensure all department heads have submitted their weekly reports', status: 'Active', priority: 'high', primary_lane: 'relationships', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 1*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          
          // Due Next Week
          { id: 'cos-t15', title: 'Prepare materials for board meeting on March 3rd', status: 'Not Started', priority: 'critical', primary_lane: 'strategy', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 8*24*60*60*1000).toISOString(), phase_count: 3, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t16', title: 'Coordinate executive travel logistics for regional site visit', status: 'Not Started', priority: 'high', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 9*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t17', title: 'Compile organizational cognitive load summary for executive monthly review', status: 'Not Started', priority: 'high', primary_lane: 'strategy', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 10*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t18', title: 'Draft agenda for Soul Food quarterly review meeting', status: 'Not Started', priority: 'high', primary_lane: 'relationships', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 7*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'soulfood' },
          { id: 'cos-t19', title: 'Follow up on three outstanding executive action items from last board meeting', status: 'Planned', priority: 'high', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 8*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t20', title: 'Ensure Platform Evolution project executive briefing is scheduled', status: 'Not Started', priority: 'medium', primary_lane: 'relationships', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 11*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          
          // Due This Month
          { id: 'cos-t21', title: 'Complete Q1 organizational performance summary for executive', status: 'Not Started', priority: 'high', primary_lane: 'strategy', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 18*24*60*60*1000).toISOString(), phase_count: 2, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t22', title: 'Coordinate executive participation in Soul Food community event', status: 'Not Started', priority: 'high', primary_lane: 'culture', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 20*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'soulfood' },
          { id: 'cos-t23', title: 'Prepare annual board calendar for executive approval', status: 'Not Started', priority: 'medium', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 22*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t24', title: 'Review and update organizational decision log for Q1', status: 'Not Started', priority: 'medium', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 25*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t25', title: 'Compile stakeholder communication plan for Q2 executive initiatives', status: 'Not Started', priority: 'high', primary_lane: 'relationships', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 28*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t26', title: 'Ensure all department heads complete Q1 self-assessments', status: 'Not Started', priority: 'medium', primary_lane: 'relationships', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 21*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'company' },
          { id: 'cos-t27', title: 'Coordinate Soul Food annual partnership review meeting', status: 'Not Started', priority: 'high', primary_lane: 'culture', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 30*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, entity: 'soulfood' }
        ],
        summary: { 
          total: 27, 
          active: 8, 
          completed_today: 1, 
          remaining_today: 5, 
          due_this_week: 8, 
          due_next_week: 6,
          company_tasks: 18,
          soulfood_tasks: 9
        }
      };
    
    case 'national_director':
      return {
        scope_label: 'National Tasks',
        tasks: [
          { id: 'nd-t1', title: 'Review Q2 National Strategy', status: 'Active', priority: 'critical', primary_lane: 'strategy', assignee: { name: 'Sarah Martinez', avatar: 'SM' }, due_date: new Date(Date.now() + 2*24*60*60*1000).toISOString(), phase_count: 4, completed_phase_count: 2 },
          { id: 'nd-t2', title: 'Cross-Regional Resource Allocation', status: 'In Review', priority: 'high', primary_lane: 'operations', assignee: { name: 'Marcus Williams', avatar: 'MW' }, due_date: new Date(Date.now() + 3*24*60*60*1000).toISOString(), phase_count: 3, completed_phase_count: 3 },
          { id: 'nd-t3', title: 'National Marketing Campaign Approval', status: 'Planned', priority: 'high', primary_lane: 'marketing', assignee: { name: 'James Chen', avatar: 'JC' }, due_date: new Date(Date.now() + 5*24*60*60*1000).toISOString(), phase_count: 2, completed_phase_count: 0 },
          { id: 'nd-t4', title: 'Budget Variance Investigation', status: 'Active', priority: 'medium', primary_lane: 'finance', assignee: { name: 'Priya Patel', avatar: 'PP' }, due_date: new Date(Date.now() + 4*24*60*60*1000).toISOString(), phase_count: 2, completed_phase_count: 1 }
        ],
        summary: { total: 24, active: 8, completed_this_week: 12, overdue: 2 }
      };
    
    case 'regional_director':
      return {
        scope_label: 'East Region Tasks',
        tasks: [
          { id: 'rd-t1', title: 'Team Capacity Rebalancing', status: 'Active', priority: 'critical', primary_lane: 'operations', assignee: { name: 'David Thompson', avatar: 'DT' }, due_date: new Date(Date.now() + 1*24*60*60*1000).toISOString(), phase_count: 3, completed_phase_count: 1 },
          { id: 'rd-t2', title: 'Regional Client QBR Prep', status: 'Active', priority: 'high', primary_lane: 'sales', assignee: { name: 'Nina Gupta', avatar: 'NG' }, due_date: new Date(Date.now() + 2*24*60*60*1000).toISOString(), phase_count: 4, completed_phase_count: 2 },
          { id: 'rd-t3', title: 'Marketing Budget Reconciliation', status: 'In Review', priority: 'medium', primary_lane: 'finance', assignee: { name: 'Alex Kim', avatar: 'AK' }, due_date: new Date(Date.now() + 3*24*60*60*1000).toISOString(), phase_count: 2, completed_phase_count: 2 },
          { id: 'rd-t4', title: 'Vendor Contract Renewal', status: 'Planned', priority: 'medium', primary_lane: 'operations', assignee: { name: 'Jessica Park', avatar: 'JP' }, due_date: new Date(Date.now() + 7*24*60*60*1000).toISOString(), phase_count: 3, completed_phase_count: 0 }
        ],
        summary: { total: 18, active: 6, completed_this_week: 8, overdue: 1 }
      };
    
    case 'unique_ip_director':
      return {
        scope_label: 'Soul Food Tasks',
        tasks: [
          { id: 'uip-t1', title: 'Nike Partnership Response', status: 'Active', priority: 'critical', primary_lane: 'partnerships', assignee: { name: 'Jordan Lee', avatar: 'JL' }, due_date: new Date(Date.now() + 1*24*60*60*1000).toISOString(), phase_count: 2, completed_phase_count: 1 },
          { id: 'uip-t2', title: 'Summer Campaign Creative Review', status: 'In Review', priority: 'high', primary_lane: 'creative', assignee: { name: 'Kai Williams', avatar: 'KW' }, due_date: new Date(Date.now() + 2*24*60*60*1000).toISOString(), phase_count: 4, completed_phase_count: 3 },
          { id: 'uip-t3', title: 'Content Calendar Q3 Planning', status: 'Active', priority: 'high', primary_lane: 'content', assignee: { name: 'Ava Chen', avatar: 'AC' }, due_date: new Date(Date.now() + 4*24*60*60*1000).toISOString(), phase_count: 3, completed_phase_count: 1 },
          { id: 'uip-t4', title: 'Pop-up Event Logistics', status: 'Planned', priority: 'medium', primary_lane: 'events', assignee: { name: 'Maya Johnson', avatar: 'MJ' }, due_date: new Date(Date.now() + 10*24*60*60*1000).toISOString(), phase_count: 5, completed_phase_count: 0 }
        ],
        summary: { total: 14, active: 5, completed_this_week: 6, overdue: 0 },
        isUniqueIP: true
      };
    
    case 'unique_ip_operations':
      return {
        scope_label: 'My Tasks',
        isUniqueIP: true,
        isOperations: true,
        isMyTasks: true,
        tasks: [
          // Due today
          { id: 'uio-t1', title: 'Complete pre-event venue walkthrough checklist', status: 'Completed', priority: 'critical', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date().toISOString(), phase_count: 1, completed_phase_count: 1, level: 'L1' },
          { id: 'uio-t2', title: 'Confirm catering delivery time with vendor', status: 'Active', priority: 'high', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date().toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'uio-t3', title: 'Set up branded signage and display materials', status: 'Active', priority: 'high', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date().toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          // Due tomorrow
          { id: 'uio-t4', title: 'Conduct weekly equipment maintenance check', status: 'Planned', priority: 'high', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 1*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'uio-t5', title: 'Submit weekly supply usage report', status: 'Not Started', priority: 'medium', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 1*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          // Due this week
          { id: 'uio-t6', title: 'Support Thursday Soul Food community dinner event', status: 'Planned', priority: 'critical', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 2*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'uio-t7', title: 'Restock merchandise display at Soul Food storefront', status: 'Planned', priority: 'medium', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 3*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'uio-t8', title: 'Coordinate post-event breakdown with team', status: 'Planned', priority: 'high', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 2*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          // Due next week
          { id: 'uio-t9', title: 'Assist with Soul Food pop-up market setup (Saturday)', status: 'Not Started', priority: 'high', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 6*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'uio-t10', title: 'Complete monthly vendor evaluation form (3 suppliers)', status: 'Not Started', priority: 'medium', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 8*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'uio-t11', title: 'Attend Soul Food operations team meeting (Monday)', status: 'Planned', priority: 'medium', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 7*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          // Due later this month
          { id: 'uio-t12', title: 'Support Soul Food quarterly community event (Mar 20)', status: 'Not Started', priority: 'high', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 28*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'uio-t13', title: 'Complete operational safety training refresher', status: 'Not Started', priority: 'medium', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 21*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'uio-t14', title: 'Submit Q1 operational efficiency notes to ops lead', status: 'Not Started', priority: 'low', primary_lane: 'operations', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 25*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' }
        ],
        summary: { total: 14, active: 3, completed_today: 1, remaining_today: 2, due_this_week: 5, due_next_week: 3 }
      };
    
    case 'marketing_manager':
      return {
        scope_label: 'Marketing Team Tasks',
        tasks: [
          { id: 'mm-t1', title: 'Campaign Performance Report', status: 'Active', priority: 'high', primary_lane: 'marketing', assignee: { name: 'Taylor Brooks', avatar: 'TB' }, due_date: new Date(Date.now() + 1*24*60*60*1000).toISOString(), phase_count: 2, completed_phase_count: 1 },
          { id: 'mm-t2', title: 'Social Content Calendar', status: 'Active', priority: 'high', primary_lane: 'content', assignee: { name: 'Casey Quinn', avatar: 'CQ' }, due_date: new Date(Date.now() + 2*24*60*60*1000).toISOString(), phase_count: 3, completed_phase_count: 2 },
          { id: 'mm-t3', title: 'Creative Asset Review', status: 'In Review', priority: 'medium', primary_lane: 'creative', assignee: { name: 'Riley Morgan', avatar: 'RM' }, due_date: new Date(Date.now() + 1*24*60*60*1000).toISOString(), phase_count: 2, completed_phase_count: 1 },
          { id: 'mm-t4', title: 'Media Vendor Coordination', status: 'Planned', priority: 'medium', primary_lane: 'marketing', assignee: { name: 'Jordan Hayes', avatar: 'JH' }, due_date: new Date(Date.now() + 4*24*60*60*1000).toISOString(), phase_count: 2, completed_phase_count: 0 }
        ],
        summary: { total: 12, active: 5, completed_this_week: 4, pending_approval: 3 }
      };
    
    case 'team_member':
      return {
        scope_label: 'My Tasks',
        isMyTasks: true,
        tasks: [
          // Due today
          { id: 'tm-t1', title: 'Write Instagram Captions', status: 'Active', priority: 'high', primary_lane: 'content', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date().toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'tm-t2', title: 'Resize Campaign Assets', status: 'Completed', priority: 'high', primary_lane: 'creative', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date().toISOString(), phase_count: 1, completed_phase_count: 1, level: 'L1' },
          { id: 'tm-t3', title: 'Review updated brand guidelines document', status: 'Active', priority: 'medium', primary_lane: 'content', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date().toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          // Due tomorrow
          { id: 'tm-t4', title: 'Submit weekly content performance report', status: 'Not Started', priority: 'high', primary_lane: 'marketing', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 1*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'tm-t5', title: 'Respond to creative brief feedback from brand lead', status: 'Planned', priority: 'high', primary_lane: 'content', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 1*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          // Due this week
          { id: 'tm-t6', title: 'Draft three blog post outlines for next month', status: 'Planned', priority: 'medium', primary_lane: 'content', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 3*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'tm-t7', title: 'Resize campaign assets for email newsletter', status: 'Planned', priority: 'medium', primary_lane: 'creative', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 3*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'tm-t8', title: 'Attend weekly marketing team sync', status: 'Planned', priority: 'medium', primary_lane: 'marketing', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 4*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'tm-t9', title: 'Complete platform onboarding training module 2', status: 'Active', priority: 'low', primary_lane: 'marketing', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 5*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          // Due next week
          { id: 'tm-t10', title: 'Prepare social media captions for March campaign batch 1', status: 'Not Started', priority: 'high', primary_lane: 'content', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 7*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'tm-t11', title: 'Coordinate with digital lead on paid social assets', status: 'Not Started', priority: 'medium', primary_lane: 'marketing', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 8*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L2' },
          { id: 'tm-t12', title: 'Submit Q1 content performance summary', status: 'Planned', priority: 'high', primary_lane: 'marketing', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 10*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          // Due later this month
          { id: 'tm-t13', title: 'Complete Q1 personal performance self-assessment', status: 'Not Started', priority: 'medium', primary_lane: 'marketing', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 14*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'tm-t14', title: 'Review and comment on brand refresh proposal', status: 'Not Started', priority: 'low', primary_lane: 'creative', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 18*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' },
          { id: 'tm-t15', title: 'Support regional marketing event setup (Mar 15)', status: 'Planned', priority: 'medium', primary_lane: 'marketing', assignee: { name: 'Me', avatar: 'ME' }, due_date: new Date(Date.now() + 23*24*60*60*1000).toISOString(), phase_count: 1, completed_phase_count: 0, level: 'L1' }
        ],
        summary: { total: 15, active: 4, completed_today: 1, deadlines_today: 3, due_this_week: 6, due_next_week: 3 }
      };
    
    default:
      return null;
  }
};

export default DemoRoleSwitcher;
